/**
 * Frame→View renderer for a portal page (ADR-0010 D12 · P7b2/P7b3).
 *
 * Takes a `PortalPageResult` (AST + RLS-resolved queries), builds the
 * interpreter `Scope` (`$user`, `$role`, `$queries`, `$page` vars), and
 * renders the component tree. Every string prop is run through the
 * sandboxed `renderTemplate` (lib/pageExpr).
 *
 * When rendered inside a `PageRuntimeProvider` (the portal), it's
 * interactive: `$page` reads live variables, `button` runs its action
 * chain, and `form` is a real submittable block. In the admin builder
 * preview (no runtime) those render inert.
 */
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BarChart3,
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Clock,
  Copy,
  DollarSign,
  Download,
  ExternalLink,
  Flag,
  GripVertical,
  Heart,
  Info,
  type LucideIcon,
  Package,
  Search,
  SearchX,
  Shield,
  ShoppingCart,
  Sofa,
  Star,
  Trash2,
  TrendingDown,
  TrendingUp,
  User,
  Users,
  X,
  Zap,
} from "lucide-react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import {
  type CSSProperties,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  createElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Bar, Doughnut, Line, Pie } from "react-chartjs-2";

import {
  type PageNode,
  type PortalComment,
  type PortalFormField,
  type PortalPageResult,
  portalApi,
} from "@/lib/api-portal";
import { pageSelectionState, togglePage, toggleSelected } from "@/lib/gridSelection";
import { humanizeColumn } from "@/lib/humanize";
import { effectiveQueryRef, isArbitraryDataBinding } from "@/lib/dataBinding";
import { type Scope, renderTemplate } from "@/lib/pageExpr";
import { type ActionContext, renderValues, runActions } from "@/lib/pageActions";
import { compileJsObjectsCached } from "@/lib/pageJsObjects";
import { type LibraryContext, useAppLibraries } from "@/lib/pageLibraries";

import { CustomComponentFrame, CustomComponentLibrariesContext } from "./CustomComponentFrame";

// Register only the Chart.js pieces the `chart` component uses (tree-shaken).
// ArcElement + Legend power pie/doughnut (3a); the rest cover bar/line/area.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Filler,
  Legend,
);

import {
  type Breakpoint,
  componentStyleClasses,
  hasStyle,
  isHidden,
  isHiddenAtBreakpoint,
  styleToClasses,
  styleToInlineStyle,
} from "@/lib/pageStyle";
import { type PortalUser, portalRowPath, usePortalAuth } from "@/lib/portalAuth";

import {
  ACTIONS_COL_WIDTH,
  type ColumnPrefs,
  MIN_COL_WIDTH,
  PINNED_COL_WIDTH,
  type PinSide,
  type SavedView,
  applyColumnOrder,
  downloadTextFile,
  loadViews,
  orderPinned,
  pageSizeOptions,
  pinnedOffset,
  saveViews,
  toCsv,
  upsertView,
} from "@/lib/dataGrid";

import { ColumnManager } from "./ColumnManager";
import { ColumnFilter, type ColumnFilterValue } from "./ColumnFilter";
import { cellText, renderCellValue } from "./widgetUi";

/** A cell's display value, preferring the server-resolved relation label
 *  (`row.__rel[col]`) over the raw id so a `relation` column shows "Acme
 *  Corp" instead of a UUID. Generic: any column without a `__rel` entry
 *  renders its raw value unchanged. */
function relValue(row: Record<string, unknown>, col: string): unknown {
  const rel = (row as { __rel?: Record<string, string> }).__rel;
  return rel && rel[col] != null ? rel[col] : row[col];
}

/** Joy-UI-style button variants (S17). `primary`/`danger` are solid, `secondary`
 *  is a soft neutral. Size + width ride on the component-style tokens (S15). */
const BUTTON_VARIANTS: Record<string, string> = {
  // Primary is the BRAND accent (`--portal-accent`, set per-app on the shell),
  // NOT a hardcoded colour — falls back to the admin accent when unbranded.
  // White label reads on any saturated brand colour.
  primary: "bg-[var(--portal-accent,hsl(var(--accent)))] text-white hover:opacity-90",
  // Outlined "soft" secondary (Asana-style) — a bordered surface chip, not a
  // grey fill — so it reads as the quieter sibling of the solid primary.
  secondary: "border border-border bg-surface text-foreground hover:bg-muted/50",
  danger: "bg-danger text-danger-foreground hover:bg-danger/90",
};

import { EmbeddedPage } from "./EmbeddedPage";
import { type GlobalFilter, type PageRuntime, useOptionalRuntime } from "./PageRuntime";
import { PortalFileViewer } from "./PortalFileViewer";
import { PortalFormBlock } from "./PortalFormBlock";
import { PortalRecordBlock } from "./PortalRecordBlock";
import { activeMentionQuery, applyMention, deriveMentions, matchMembers } from "@/lib/mentions";

function buildScope(result: PortalPageResult, rt: PageRuntime | null, lib?: LibraryContext): Scope {
  const src = rt ? rt.queries : result.queries;
  const queries: Record<string, unknown> = {};
  for (const [name, page] of Object.entries(src)) {
    // table_id rides along so the data_list row-action delete can target the
    // right table (S5); `data` (ADR-0022) carries an operation/static resource's
    // value so `{{ $queries.<name>.data }}` resolves, beyond just `rows`.
    queries[name] = {
      rows: page.rows,
      total: page.total,
      table_id: page.table_id,
      data: page.data,
    };
  }
  let pageVars: Record<string, unknown>;
  if (rt) {
    pageVars = rt.vars;
  } else {
    pageVars = {};
    for (const v of result.page.ast.variables ?? []) pageVars[v.name] = v.initial ?? null;
  }
  // JS Objects (ADR-0044 J1): the page's authored client logic, compiled ONCE
  // (cached by the AST array identity) into `{ <name>: namespace }` and injected
  // as top-level scope entries so a binding `{{ <name>.fn(args) }}` resolves.
  // Works in both the live runtime and the builder preview (rt may be null).
  // Inject the external libraries (ADR-0046) into the JS-Object compile too, so a
  // JS Object body can reference `_`/`dayjs`/… (cache keyed on (defs, lib)).
  const { namespaces } = compileJsObjectsCached(result.page.ast.js_objects, lib);
  return {
    $user: rt ? rt.user : result.user,
    $role: rt ? rt.role : result.role,
    $queries: queries,
    $page: pageVars,
    // ADR-0046 — the app's external library globals (lodash `_`, dayjs, …),
    // already marked trusted, spread as top-level scope entries so a binding
    // `{{ _.sumBy(rows, 'x') }}` resolves. JS Object namespaces spread AFTER, so
    // a JS Object name wins a collision with a library global.
    ...(lib?.globals ?? {}),
    // App-scoped global store (ADR-0043) — survives navigation. In the builder
    // preview (no runtime) it falls back to the DECLARED `$global` initials
    // (WS-4a), mirroring how `$page` falls back to the AST variables above, so
    // `{{ $global.<name> }}` resolves on the canvas.
    $global: rt
      ? rt.store
      : Object.fromEntries((result.globalVariables ?? []).map((v) => [v.name, v.initial ?? null])),
    // Read-only context when this page is opened as an overlay (ADR-0023); `{}`
    // for a normally-navigated page (and in the builder preview).
    $input: rt ? rt.input : {},
    ...namespaces,
  };
}

/** Build the full action-runtime `ActionContext` from the live runtime + a
 *  scope. The SINGLE place the verb SDK is wired (button `on_click` AND the
 *  per-widget event handlers, ADR-0045 F4.2, all share it), so a new verb is
 *  added in one spot. Server writes are bound to this app's slug + write-policy
 *  gated server-side; `mode` lets the builder's running canvas suppress
 *  navigation / server actions (R3). */
function buildActionContext(rt: PageRuntime, scope: Scope): ActionContext {
  return {
    scope,
    setVar: rt.setVar,
    refresh: rt.refresh,
    navigate: rt.navigate,
    toast: rt.toast,
    createRow: (tableId, data) => portalApi.createRow(rt.appSlug, tableId, data),
    updateRow: (tableId, rowId, data) => portalApi.updateRow(rt.appSlug, tableId, rowId, data),
    deleteRow: (tableId, rowId) => portalApi.deleteRow(rt.appSlug, tableId, rowId),
    confirm: (m) => window.confirm(m),
    runWorkflow: (wfId, payload) => portalApi.runWorkflow(rt.appSlug, wfId, payload),
    runQuery: rt.runQuery,
    // ADR-0060 S5 — role-gated report render (the server re-checks the grant).
    // R2: when the Type declares render params the action's static values don't
    // cover, ask the END USER via the runtime's guided dialog (pre-filled with
    // the static ones); cancel aborts the print. A declaration-less report (or
    // a fully bound action) renders directly — no dialog, the S5 behaviour.
    printReport: async (reportId, params) => {
      const reports = await portalApi.listReports(rt.appSlug).catch(() => null);
      const schema = reports?.find((r) => r.id === reportId)?.param_schema ?? [];
      const unset = schema.filter((p) => {
        const v = params[p.name];
        return v === undefined || v === null || v === "";
      });
      let values = params;
      if (unset.length > 0 && rt.promptReportParams) {
        const out = await rt.promptReportParams({
          title: reports?.find((r) => r.id === reportId)?.name ?? "",
          schema,
          initial: params,
          // R2b — dynamic options feed for a param that declares a source
          fetchOptions: (p) =>
            portalApi.reportParamOptions(rt.appSlug, reportId, p).then((r) => r.options),
        });
        if (out === null) return; // the user cancelled — no render
        values = out;
      }
      await portalApi.printReport(rt.appSlug, reportId, values);
    },
    openDrawer: (page, vars) => rt.openDrawer(page, vars ?? {}),
    openOverlay: rt.openOverlay,
    closeOverlay: rt.closeOverlay,
    storeValue: rt.storeValue,
    removeValue: rt.removeValue,
    clearStore: rt.clearStore,
    openModal: rt.openModal,
    closeModal: rt.closeModal,
    mode: rt.mode,
  };
}

/** Fire a per-widget EVENT handler (ADR-0045 F4.2): run its action chain with
 *  `$event` MERGED into the scope, so the handler reads `{{ $event.value }}` /
 *  `{{ $event.row }}` and a `run_js` handler gets `$event` as a binding. No-op
 *  when the chain is empty / there's no runtime (builder canvas without `rt`).
 *  Same `ActionContext` as a click → identical gating + verb surface. */
function fireEvent(
  chain: unknown,
  rt: PageRuntime | null,
  scope: Scope,
  event: Record<string, unknown>,
): void {
  if (!rt || !Array.isArray(chain) || chain.length === 0) return;
  const eventScope = { ...(scope as Record<string, unknown>), $event: event } as Scope;
  void runActions(chain, buildActionContext(rt, eventScope));
}

/** Resolve a prop: render `{{ }}` templates against the scope, else pass
 *  through. A bad binding (shouldn't happen — save-time validated) degrades
 *  to an empty string rather than crashing the whole page. */
function tmpl(value: unknown, scope: Scope): unknown {
  if (typeof value !== "string" || !value.includes("{{")) return value;
  try {
    return renderTemplate(value, scope);
  } catch {
    return "";
  }
}

function str(value: unknown, scope: Scope): string {
  const v = tmpl(value, scope);
  return v == null ? "" : String(v);
}

/** Props whose STRING value is a raw template the component interprets itself —
 *  resolving them here would corrupt them. `data` is a query-ref binding read by
 *  `effectiveQueryRef` (it needs the raw `{{ $queries.x }}`); `code` is a custom
 *  component's JSX source (its inline styles literally contain `{{ }}`). */
const RAW_BINDING_PROPS = new Set(["data", "code"]);

/** Resolve EVERY scalar `{{ }}` binding in a node's props ONCE (ADR-0044 U4 —
 *  the runtime half of the inspector's per-field JS toggle). A prop whose value
 *  is a `{{ }}` string is evaluated against the scope and its typed result
 *  (number/bool/string) replaces the template — so a `Visible = {{ row.id > 0 }}`
 *  / `page_size = {{ $page.n }}` binding is honoured by the SAME `node.props.X`
 *  reads the components already do, with NO per-site rewrite. Structural props
 *  (`RAW_BINDING_PROPS`), non-strings, and literals pass through untouched; a
 *  half-typed binding that throws keeps its raw value (the builder never crashes
 *  mid-edit). Returns the SAME object when nothing resolved (cheap, ref-stable). */
function resolveScalarBindings(
  props: Record<string, unknown>,
  scope: Scope,
): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  for (const key in props) {
    const v = props[key];
    if (typeof v !== "string" || !v.includes("{{") || RAW_BINDING_PROPS.has(key)) continue;
    try {
      const resolved = renderTemplate(v, scope);
      if (resolved !== v) {
        if (!out) out = { ...props };
        out[key] = resolved;
      }
    } catch {
      /* a mid-edit / bad binding keeps its raw value — never crash the page */
    }
  }
  return out ?? props;
}

/** The evaluated rows for an ARBITRARY `data` array binding (ADR-0044 U1) —
 *  e.g. `{{ Deals.fn() }}` / `{{ $queries.x.rows.filter(...) }}`. Returns [] when
 *  `data` isn't an array expression (a plain query ref reads `q.rows` instead). */
function arbitraryDataRows(node: PageNode, scope: Scope): Record<string, unknown>[] {
  if (!isArbitraryDataBinding(node.props)) return [];
  const v = tmpl(node.props.data, scope);
  return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
}

interface RowAction {
  label?: string;
  action?: "open" | "edit" | "delete" | "select";
  target_page?: string;
  open_mode?: "page" | "drawer";
}

/** Data grid 2b — a compact in-cell editor for the data_list inline edit. Holds
 *  its own draft, commits on Enter/blur, cancels on Escape. Only simple scalar
 *  widgets edit inline (relation/file edit via the record drawer). */
function InlineCellEditor({
  field,
  initial,
  busy,
  onCommit,
  onCancel,
}: {
  field: PortalFormField;
  initial: unknown;
  busy: boolean;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
}): ReactNode {
  const [draft, setDraft] = useState<unknown>(initial ?? "");
  const cls = "w-full rounded border border-accent bg-background px-1.5 py-1 text-sm outline-none";
  const onKey = (e: { key: string }) => {
    if (e.key === "Enter") onCommit(draft);
    if (e.key === "Escape") onCancel();
  };
  if (field.widget === "checkbox") {
    return (
      <input
        autoFocus
        type="checkbox"
        disabled={busy}
        checked={Boolean(draft)}
        // A toggle commits immediately (no separate blur needed).
        onChange={(e) => onCommit(e.target.checked)}
      />
    );
  }
  if (field.widget === "select") {
    return (
      <select
        autoFocus
        className={cls}
        disabled={busy}
        value={draft == null ? "" : String(draft)}
        onChange={(e) => onCommit(e.target.value || null)}
        onKeyDown={onKey}
        onBlur={() => onCommit(draft)}
      >
        <option value="">—</option>
        {(field.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  const type =
    field.widget === "number"
      ? "number"
      : field.widget === "datetime"
        ? "datetime-local"
        : field.widget === "date" || field.widget === "email" || field.widget === "url"
          ? field.widget
          : "text";
  const raw = draft == null ? "" : String(draft);
  return (
    <input
      autoFocus
      type={type}
      className={cls}
      disabled={busy}
      value={field.widget === "datetime" ? raw.slice(0, 16) : raw}
      onChange={(e) =>
        setDraft(
          field.widget === "number"
            ? e.target.value === ""
              ? null
              : Number(e.target.value)
            : e.target.value,
        )
      }
      onKeyDown={onKey}
      onBlur={() => onCommit(draft)}
    />
  );
}

/** Grid-card thumbnail. Accepts EITHER a plain image URL string OR an app
 *  `file`-column descriptor (`{ ref, filename, … }`): a descriptor is fetched
 *  as an authenticated blob (the attachment URL needs a Bearer header, so it
 *  can't ride `<img src>` directly — same seam as `PortalFileViewer`), a URL is
 *  used verbatim. A missing/blank value renders the neutral sofa placeholder. */
function CardThumb({ appSlug, value, alt }: { appSlug: string; value: unknown; alt: string }) {
  const url = typeof value === "string" && /^https?:\/\//.test(value) ? value : "";
  const ref =
    !url &&
    value &&
    typeof value === "object" &&
    typeof (value as { ref?: unknown }).ref === "string"
      ? ((value as { ref: string }).ref as string)
      : "";
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!ref || !appSlug) {
      setBlobUrl(null);
      return;
    }
    let obj: string | null = null;
    let alive = true;
    portalApi
      .attachmentBlob(appSlug, ref)
      .then((blob) => {
        if (!alive) return;
        obj = URL.createObjectURL(blob);
        setBlobUrl(obj);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [appSlug, ref]);
  const src = url || blobUrl;
  if (src) return <img src={src} alt={alt} className="h-full w-full object-cover" />;
  return (
    // Missing image → a minimal, brand-neutral placeholder: a faint muted panel
    // with a line-drawn sofa "sinottico" (no colour, no initials).
    <span className="absolute inset-0 flex items-center justify-center bg-muted/40">
      <Sofa className="h-10 w-10 text-muted-foreground/35" strokeWidth={1.25} />
    </span>
  );
}

function DataList({
  node,
  scope,
  rt,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
}): ReactNode {
  // ADR-0044 U1 — the data source is a `{{ }}` binding (`data`). When the
  // binding is a plain query ref we keep the FULL reactive query (server
  // search/sort/pagination via `ref`); an arbitrary array binding
  // (`{{ Deals.fn() }}`) is evaluated to a static list.
  const ref = effectiveQueryRef(node.props);
  const q = (
    scope.$queries as Record<
      string,
      { rows: Record<string, unknown>[]; total?: number; table_id?: string }
    >
  )[ref];
  const rows: Record<string, unknown>[] = ref ? (q?.rows ?? []) : arbitraryDataRows(node, scope);
  const actions = (
    Array.isArray(node.props.row_actions) ? node.props.row_actions : []
  ) as RowAction[];

  // Live-data controls (S16 / S16b): user search / sort / pagination.
  const searchable = node.props.searchable === true;
  const sortable = node.props.sortable === true;
  const filterable = node.props.filterable === true;
  // Per-column filter config (admin-set, G1): { <col>: { filter, options } }.
  // Unconfigured columns default to a cheap text-contains; "none" hides it.
  const columnConfig = (node.props.column_config ?? {}) as Record<
    string,
    {
      filter?: "none" | "text" | "select" | "range" | "daterange";
      options?: string[];
      hidden?: boolean;
      pin?: "left" | "right";
    }
  >;
  // User column management (the "Columns" menu): show/hide (G2), pin (G3),
  // reorder (G4). Each is gated on its own master toggle; the admin sets the
  // defaults (column_config.hidden / .pin + the `columns` order) and the user
  // overrides them at runtime, persisted per-user in localStorage.
  const canHide = node.props.column_chooser === true;
  const canPin = node.props.pinnable === true;
  const canReorder = node.props.reorderable === true;
  const canResize = node.props.resizable === true;
  const exportable = node.props.exportable === true;
  // Data grid 2b — opt-in inline cell edit. The admin picks which columns are
  // editable (empty = all simple-scalar ones). Writes go through the portal
  // updateRow endpoint (RLS + write-policy gated server-side).
  const inlineEdit = node.props.inline_edit === true;
  const editableCols = Array.isArray(node.props.editable_columns)
    ? (node.props.editable_columns as string[])
    : [];
  // The "Columns" menu hosts the Reset that also clears widths, so it shows when
  // any per-user column feature (incl. resize) is on.
  const colMenuEnabled = canHide || canPin || canReorder || canResize;
  // Pagination (S16) — the admin enables it with `page_size`; the user can then
  // pick a different rows-per-page at runtime (G9), persisted in colPrefs.
  const adminPageSize = Math.max(0, Number(node.props.page_size ?? 0) || 0);
  const hasControls = searchable || sortable || filterable || colMenuEnabled || adminPageSize > 0;
  // In the portal runtime (rt) we re-query the SERVER over the FULL dataset
  // (debounced); in the builder preview (no rt) we fall back to client-side
  // over the loaded sample rows so the controls still demonstrate.
  // Server-side controls need a real query `ref`; an arbitrary array binding
  // (no ref) is filtered/sorted/paginated client-side over the evaluated rows.
  const serverMode = !!rt && hasControls && !!ref;
  // A REACTIVE query (declares `deps` — its `{{ $page }}` filter, ADR-0022 Phase C)
  // in server mode must carry its EVALUATED `$page` filters into the S16b fetch:
  // that fetch is otherwise blind to `$page` (and the backend drops the raw `{{ }}`
  // template), so a versione-filtered master-detail would come back empty (the
  // "searchable master-detail shows nothing" bug). Merging the concrete filter
  // keeps server-side search/sort/pagination correct AND scalable to any row count.
  const reactive = !!ref && !!rt?.reactiveQueryNames?.has(ref);
  const reactiveFilters =
    serverMode && reactive && rt ? (rt.reactiveParams(ref)?.filters ?? []) : [];
  // Re-fetch when the evaluated `$page` filter changes (a new selection) — this is
  // the signal that a reactive server-mode list must re-query on.
  const reactiveFiltersKey = JSON.stringify(reactiveFilters);

  // Hooks run unconditionally (before any early return) — React rule.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [colFilters, setColFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  // Inline-edit state (2b): the table's editable field specs (by column name),
  // which cell is open, and the in-flight save flag.
  const tableId = q?.table_id ?? "";
  const [editFields, setEditFields] = useState<Record<string, PortalFormField>>({});
  const [editCell, setEditCell] = useState<{ col: string; rowId: string } | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  // Data grid 2a (portal) — checkbox selection. MUST live in the unconditional
  // hooks block (before any early return), like the other state above.
  const [selIds, setSelIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const editColsKey = editableCols.join(",");
  useEffect(() => {
    if (!inlineEdit || !rt || !tableId) return;
    let alive = true;
    portalApi
      .getTableSchema(rt.appSlug, tableId)
      .then((s) => {
        if (!alive) return;
        const allow = new Set(editColsKey ? editColsKey.split(",") : []);
        const map: Record<string, PortalFormField> = {};
        for (const f of s.columns) {
          // Only simple scalar widgets edit inline; relation/file → record drawer.
          if (f.widget === "relation" || f.widget === "relation_many" || f.widget === "file")
            continue;
          // Computed columns (lookup/formula) are read-only — never editable.
          if (f.kind === "lookup" || f.kind === "formula") continue;
          if (allow.size === 0 || allow.has(f.name)) map[f.name] = f;
        }
        setEditFields(map);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [inlineEdit, rt, tableId, editColsKey]);

  // relation_many column filter (D20, op:"has") — load the related-row choices
  // for every relation_many column so the grid filter is a single-select over
  // real rows instead of a useless free-text on a jsonb id array.
  const [relFilterOpts, setRelFilterOpts] = useState<
    Record<string, { id: string; label: string }[]>
  >({});
  useEffect(() => {
    if (!rt || !tableId || !filterable) return;
    let alive = true;
    portalApi
      .getTableSchema(rt.appSlug, tableId)
      .then(async (s) => {
        const relCols = s.columns.filter((f) => f.widget === "relation_many" && f.target_table);
        const out: Record<string, { id: string; label: string }[]> = {};
        await Promise.all(
          relCols.map(async (f) => {
            try {
              out[f.name] = await portalApi.tableOptions(rt.appSlug, String(f.target_table));
            } catch {
              /* skip a target the caller can't read */
            }
          }),
        );
        if (alive) setRelFilterOpts(out);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [rt, tableId, filterable]);

  const commitEdit = async (col: string, rowId: string, value: unknown) => {
    if (!rt || !tableId) {
      setEditCell(null);
      return;
    }
    setEditBusy(true);
    try {
      await portalApi.updateRow(rt.appSlug, tableId, rowId, { [col]: value });
      setEditCell(null);
      rt.refresh();
    } catch {
      // Keep the editor open so the user can correct the value.
    } finally {
      setEditBusy(false);
    }
  };
  // Column manager prefs (G2 hide / G3 pin / G4 order), seeded from the user's
  // saved choice (localStorage, per app+page+query) or — first visit — the
  // admin defaults (column_config.hidden / .pin; order defers to the `columns`
  // prop, so the default `order` is empty = natural).
  const adminDefaults = (): ColumnPrefs => ({
    hidden: Object.entries(columnConfig)
      .filter(([, c]) => c?.hidden)
      .map(([col]) => col),
    pinned: Object.fromEntries(
      Object.entries(columnConfig)
        .filter(([, c]) => c?.pin)
        .map(([col, c]) => [col, c.pin as PinSide]),
    ),
    order: [],
    widths: {},
  });
  const prefsKey = rt ? `tray:colprefs:${rt.appSlug}:${rt.pageSlug}:${ref}` : "";
  const [colPrefs, setColPrefs] = useState<ColumnPrefs>(() => {
    if (prefsKey && typeof localStorage !== "undefined") {
      try {
        const raw = localStorage.getItem(prefsKey);
        if (raw) {
          const p = JSON.parse(raw) as Partial<ColumnPrefs> & { pinned?: unknown };
          // Tolerate a legacy `pinned: string[]` (G3/G4) → map each to "left".
          const pinned = Array.isArray(p.pinned)
            ? Object.fromEntries((p.pinned as string[]).map((c) => [c, "left" as PinSide]))
            : ((p.pinned as Record<string, PinSide>) ?? {});
          return {
            hidden: p.hidden ?? [],
            pinned,
            order: p.order ?? [],
            widths: p.widths ?? {},
            pageSize: p.pageSize,
          };
        }
      } catch {
        /* ignore corrupt storage */
      }
    }
    return adminDefaults();
  });
  useEffect(() => {
    if (prefsKey && typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(prefsKey, JSON.stringify(colPrefs));
      } catch {
        /* ignore quota errors */
      }
    }
  }, [prefsKey, colPrefs]);
  // Saved views (G10): named ColumnPrefs snapshots, persisted per-user next to
  // the live prefs. Runtime-only (needs a stable storage key).
  const viewsKey = rt ? `tray:colviews:${rt.appSlug}:${rt.pageSlug}:${ref}` : "";
  const [views, setViews] = useState<SavedView[]>(() => loadViews(viewsKey));
  useEffect(() => {
    if (viewsKey) saveViews(viewsKey, views);
  }, [viewsKey, views]);

  // Server mirror (G10): when the portal user is logged in, the prefs + views
  // live on the app_user (durable + cross-device). localStorage above stays as
  // the instant cache; the server is the source of truth on first load and a
  // debounced write target after. Anonymous / builder → localStorage only.
  const token = usePortalAuth((s) => s.token);
  const appSlug = rt?.appSlug ?? "";
  const serverKey = rt ? `grid:${rt.appSlug}:${rt.pageSlug}:${ref}` : "";
  const serverHydrated = useRef(false);
  const serverSaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (!token || !appSlug || !serverKey) return;
    let cancelled = false;
    portalApi
      .getPreferences(appSlug)
      .then((res) => {
        if (cancelled) return;
        const v = (res.preferences ?? {})[serverKey] as
          | { prefs?: ColumnPrefs; views?: SavedView[] }
          | undefined;
        if (v?.prefs) setColPrefs(v.prefs);
        if (Array.isArray(v?.views)) setViews(v.views);
      })
      .catch(() => {
        /* offline / not a member — fall back to the localStorage cache */
      })
      .finally(() => {
        serverHydrated.current = true;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, appSlug, serverKey]);
  useEffect(() => {
    // Only write AFTER the initial server read, so we never clobber the user's
    // saved layout with the localStorage seed before we've read it.
    if (!token || !appSlug || !serverKey || !serverHydrated.current) return;
    clearTimeout(serverSaveTimer.current);
    serverSaveTimer.current = setTimeout(() => {
      portalApi
        .patchPreferences(appSlug, { [serverKey]: { prefs: colPrefs, views } })
        .catch(() => {});
    }, 700);
    return () => clearTimeout(serverSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, appSlug, serverKey, colPrefs, views]);
  const toggleHidden = (col: string) =>
    setColPrefs((p) => {
      const has = p.hidden.includes(col);
      return { ...p, hidden: has ? p.hidden.filter((c) => c !== col) : [...p.hidden, col] };
    });
  // Effective rows-per-page: the user's choice (G9) or the admin's `page_size`.
  const pageSize = adminPageSize > 0 ? (colPrefs.pageSize ?? adminPageSize) : 0;
  const setPageSize = (n: number) => setColPrefs((p) => ({ ...p, pageSize: n }));
  // Cycle a column's pin: none → left → right → none (G6).
  const cyclePin = (col: string) =>
    setColPrefs((p) => {
      const next = { ...p.pinned };
      const cur = next[col];
      if (!cur) next[col] = "left";
      else if (cur === "left") next[col] = "right";
      else delete next[col];
      return { ...p, pinned: next };
    });
  // Column resize (G7): drag a header's right edge. We measure the live header
  // width on mousedown, then write the new width to colPrefs on each move.
  const resizeRef = useRef<{ col: string; startX: number; startW: number } | null>(null);
  const startResize = (e: ReactMouseEvent, col: string) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).closest("th");
    const startW = th?.getBoundingClientRect().width ?? colPrefs.widths[col] ?? 160;
    resizeRef.current = { col, startX: e.clientX, startW };
    const onMove = (ev: MouseEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      const w = Math.max(MIN_COL_WIDTH, Math.round(r.startW + (ev.clientX - r.startX)));
      setColPrefs((p) => ({ ...p, widths: { ...p.widths, [r.col]: w } }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // Per-column filters (G1) + the dashboard-wide global filter (3b) → server
  // `filters` list, ANDed with search/static. The global filter is keyed
  // separately so a chart-driven drill-down re-fetches this list too.
  const globalKey = JSON.stringify(rt?.globalFilters ?? []);
  const filtersKey = JSON.stringify(colFilters) + globalKey;
  // A `range` filter expands to gte/lte conditions (ANDed server-side); the
  // other ops map 1:1.
  const queryFilters = [
    ...Object.entries(colFilters).flatMap(([column, f]) => {
      if (f.op === "range") {
        const out: { column: string; op: string; value: unknown }[] = [];
        if (f.min) out.push({ column, op: "gte", value: f.min });
        if (f.max) out.push({ column, op: "lte", value: f.max });
        return out;
      }
      return [{ column, op: f.op, value: f.value }];
    }),
    ...(rt?.globalFilters ?? []).map((f) => ({ column: f.column, op: f.op, value: f.value })),
  ];
  // Seed the server view from the statically-loaded first page so the initial
  // paint is instant (no flash) before the reactive fetch returns.
  const [serverData, setServerData] = useState<{ rows: Record<string, unknown>[]; total: number }>(
    () => ({ rows: q?.rows ?? [], total: q?.total ?? q?.rows?.length ?? 0 }),
  );

  // Debounce the search box so we don't query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  // Reset to page 1 whenever the result-set shape changes (incl. a new reactive
  // `$page` selection — a different parent means a different filtered set).
  useEffect(
    () => setPage(1),
    [debouncedSearch, sortCol, sortDir, filtersKey, reactiveFiltersKey, pageSize],
  );

  // The static query result is stable until the page refreshes — using it as a
  // dep re-fetches the live view after a write (refresh).
  const refreshKey = rt?.queries[ref];
  useEffect(() => {
    if (!serverMode || !rt) return;
    let cancelled = false;
    const sortBody = sortCol ? [{ column: sortCol, descending: sortDir === "desc" }] : [];
    portalApi
      .queryPageData(rt.appSlug, rt.pageSlug, ref, {
        search: debouncedSearch.trim() || undefined,
        // A reactive query's evaluated `$page` filters ride FIRST, then the user's
        // column/global filters (both ANDed server-side).
        filters: [...reactiveFilters, ...queryFilters],
        sort: sortBody,
        page,
        page_size: pageSize > 0 ? pageSize : undefined,
      })
      .then((res) => {
        if (!cancelled) setServerData({ rows: res.rows, total: res.total });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serverMode,
    ref,
    debouncedSearch,
    sortCol,
    sortDir,
    filtersKey,
    reactiveFiltersKey,
    page,
    pageSize,
    refreshKey,
  ]);

  // In server mode the rows come from the server fetch (`serverData`), NOT the
  // engine-loaded `q.rows` — so the empty guard + column inference read from
  // there (a reactive server-mode list has an empty `q.rows` but real
  // `serverData.rows`, which otherwise showed "No data." over real rows).
  const baseRows = serverMode ? serverData.rows : rows;
  if (baseRows.length === 0) {
    // A search / column-filter that matched NOTHING must keep its controls
    // visible — else the user can't clear the query to get results back (the
    // bug: "search 0 → the search box vanishes"). Only the GENUINE empty state
    // (no active query) collapses to the bare message.
    const hasActiveQuery =
      (searchable && search.trim() !== "") || (filterable && Object.keys(colFilters).length > 0);
    if (!hasActiveQuery) {
      return (
        <p className="text-sm text-muted-foreground italic">
          {str(node.props.empty_text, scope) || "No data."}
        </p>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {searchable && (
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              placeholder="Search…"
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-[var(--portal-radius)] border border-border bg-surface py-1.5 pl-8 pr-3 text-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>
        )}
        {filterable && Object.keys(colFilters).length > 0 && (
          <button
            type="button"
            onClick={() => setColFilters({})}
            className="self-start text-xs font-medium text-accent hover:underline"
          >
            Clear filters ({Object.keys(colFilters).length})
          </button>
        )}
        <p className="py-6 text-center text-sm text-muted-foreground italic">
          {str(node.props.empty_text, scope) || "No results."}
        </p>
      </div>
    );
  }
  const explicit = Array.isArray(node.props.columns) ? (node.props.columns as string[]) : [];
  const allColumns = explicit.length > 0 ? explicit : Object.keys(baseRows[0]);
  // Resolve the displayed columns from the user prefs (each step gated on its
  // master toggle): G4 order → G2 visibility → G3 pinned-first. Pinned columns
  // float to the left edge regardless of the user's drag order.
  const hiddenSet = new Set(colPrefs.hidden);
  const orderedAll = canReorder ? applyColumnOrder(allColumns, colPrefs.order) : allColumns;
  const visibleColumns = canHide ? orderedAll.filter((c) => !hiddenSet.has(c)) : orderedAll;
  const pinOf = (c: string): PinSide | null => (canPin ? (colPrefs.pinned[c] ?? null) : null);
  const columns = canPin ? orderPinned(visibleColumns, pinOf) : visibleColumns;
  // Effective width (G7): user-set width if any; pinned columns fall back to the
  // fixed PINNED_COL_WIDTH (so their sticky offsets stay deterministic);
  // unpinned columns fall back to auto (undefined).
  const widthOf = (c: string): number | undefined =>
    colPrefs.widths[c] ?? (pinOf(c) ? PINNED_COL_WIDTH : undefined);
  const fixedW = (c: string) => widthOf(c) ?? PINNED_COL_WIDTH;
  // Right-pinned columns sit just inside the actions column, which itself pins
  // right when any data column is right-pinned (so the buttons stay visible).
  const hasRightPin = columns.some((c) => pinOf(c) === "right");
  const pinActions = hasRightPin && actions.length > 0;
  const rightBase = pinActions ? ACTIONS_COL_WIDTH : 0;
  const lastLeftPin = columns.filter((c) => pinOf(c) === "left").at(-1);
  const firstRightPin = columns.find((c) => pinOf(c) === "right");
  // Width style for any (incl. unpinned) cell — a fixed width once the user has
  // resized it; otherwise auto.
  const widthStyle = (c: string): CSSProperties | undefined => {
    const w = widthOf(c);
    return w == null ? undefined : { width: w, minWidth: w, maxWidth: w };
  };
  // Sticky placement + opaque bg for a pinned cell (head/body share it). A
  // box-shadow divider marks the freeze boundary (border-collapse drops a real
  // border under position: sticky).
  const pinProps = (c: string): { className: string; style: CSSProperties | undefined } => {
    const off = pinnedOffset(columns, pinOf, c, rightBase, fixedW);
    if (!off) return { className: "", style: widthStyle(c) };
    const divider =
      off.side === "left"
        ? c === lastLeftPin
          ? " shadow-[inset_-1px_0_0_hsl(var(--border))]"
          : ""
        : c === firstRightPin
          ? " shadow-[inset_1px_0_0_hsl(var(--border))]"
          : "";
    const W = fixedW(c);
    return {
      className: `sticky z-10 bg-surface${divider}`,
      style:
        off.side === "left"
          ? { left: off.px, width: W, minWidth: W, maxWidth: W }
          : { right: off.px, width: W, minWidth: W, maxWidth: W },
    };
  };
  // The row-actions column, pinned right when data columns are right-pinned.
  const actionsPin: { className: string; style: CSSProperties | undefined } = pinActions
    ? {
        className: " sticky right-0 z-10 bg-surface shadow-[inset_1px_0_0_hsl(var(--border))]",
        style: {
          width: ACTIONS_COL_WIDTH,
          minWidth: ACTIONS_COL_WIDTH,
          maxWidth: ACTIONS_COL_WIDTH,
        },
      }
    : { className: "", style: undefined };

  // Resolve the rows to show + the total. Server mode already filtered/sorted/
  // paginated; client mode does it over the loaded rows.
  let visible: Record<string, unknown>[];
  let effTotal: number;
  if (serverMode) {
    visible = serverData.rows;
    effTotal = serverData.total;
  } else {
    // Client mode (builder preview): filter immediately (cheap, no debounce).
    const q2 = search.trim().toLowerCase();
    const filtered = q2
      ? rows.filter((r) => columns.some((c) => formatCell(r[c]).toLowerCase().includes(q2)))
      : rows;
    const sorted = sortCol
      ? [...filtered].sort((a, b) => {
          const av = a[sortCol];
          const bv = b[sortCol];
          let cmp: number;
          if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
          else cmp = formatCell(av).localeCompare(formatCell(bv), undefined, { numeric: true });
          return sortDir === "asc" ? cmp : -cmp;
        })
      : filtered;
    effTotal = sorted.length;
    const tp = pageSize > 0 ? Math.max(1, Math.ceil(effTotal / pageSize)) : 1;
    const cp = Math.min(Math.max(1, page), tp);
    visible = pageSize > 0 ? sorted.slice((cp - 1) * pageSize, cp * pageSize) : sorted;
  }
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(effTotal / pageSize)) : 1;
  const curPage = Math.min(Math.max(1, page), totalPages);

  function toggleSort(col: string) {
    if (!sortable) return;
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  }
  // CSV export (G8): export the CURRENT view — the visible/ordered columns + the
  // full filtered/sorted dataset. In the runtime we paginate the reactive query
  // (server caps page_size at 200) up to a soft cap; the builder preview exports
  // the loaded sample. Honours the active search + column filters + sort.
  async function exportCsv() {
    setExporting(true);
    try {
      let data: Record<string, unknown>[];
      if (rt) {
        data = [];
        const sortBody = sortCol ? [{ column: sortCol, descending: sortDir === "desc" }] : [];
        const PAGE = 200;
        const MAX_PAGES = 50; // soft cap: 10k rows
        for (let pg = 1; pg <= MAX_PAGES; pg++) {
          const res = await portalApi.queryPageData(rt.appSlug, rt.pageSlug, ref, {
            search: debouncedSearch.trim() || undefined,
            filters: queryFilters,
            sort: sortBody,
            page: pg,
            page_size: PAGE,
          });
          data.push(...res.rows);
          if (data.length >= res.total || res.rows.length < PAGE) break;
        }
      } else {
        data = visible;
      }
      downloadTextFile(`${ref || "export"}.csv`, toCsv(columns, data));
    } catch {
      /* export is best-effort; a failed fetch just doesn't download */
    } finally {
      setExporting(false);
    }
  }
  // Master-detail selection (S13): when `select_into` is set, clicking a row
  // writes the whole row into that page variable. The currently-selected id is
  // read back from the same var so the row can highlight.
  const selectInto = String(node.props.select_into ?? "").trim();
  const selectedId =
    selectInto && rt
      ? String((rt.vars[selectInto] as Record<string, unknown> | undefined)?.id ?? "")
      : "";
  const selectRow = (row: Record<string, unknown>) => {
    if (rt && selectInto) rt.setVar(selectInto, row);
    // F4.2: fire the row-select handler with `$event.row` = the clicked row.
    fireEvent(node.props.on_row_select, rt, scope, { row });
  };

  // Data grid 2a (portal) — checkbox selection, configurable from the editor:
  // "none" (default) | "single" | "multiple". A density toggle ("compact")
  // tightens the rows. Row-click select_into stays the behaviour ONLY in
  // "none" mode (back-compat); otherwise the checkbox column drives it.
  const selectionMode = (() => {
    const m = String(node.props.selection_mode ?? "none");
    return m === "single" || m === "multiple" ? m : "none";
  })();
  const compact = node.props.compact === true;
  const padY = compact ? "py-1" : "py-3";
  // Write the current selection into the `select_into` page var so a button can
  // act on it: a single row object in "single" mode, an array in "multiple".
  const applySelection = (next: Set<string>, source: Record<string, unknown>[]) => {
    setSelIds(next);
    if (!rt || !selectInto) return;
    if (selectionMode === "single") {
      const id = [...next][0];
      const row = source.find((r) => String(r.id ?? "") === id) ?? null;
      rt.setVar(selectInto, row);
      if (row) fireEvent(node.props.on_row_select, rt, scope, { row });
    } else {
      rt.setVar(
        selectInto,
        source.filter((r) => next.has(String(r.id ?? ""))),
      );
    }
  };
  // Opt-in bulk delete: fire the per-row portal deleteRow sequentially (each
  // honours the row's delete policy + on-delete cascade), then refresh + clear.
  const bulkAllowed = node.props.bulk_delete === true && selectionMode === "multiple";
  const bulkDelete = async () => {
    const tid = q?.table_id;
    const ids = [...selIds];
    if (!rt || !tid || ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected record(s)?`)) return;
    setBulkBusy(true);
    try {
      for (const id of ids) await portalApi.deleteRow(rt.appSlug, tid, id);
      setSelIds(new Set());
      if (selectInto) rt.setVar(selectInto, []);
      rt.refresh();
    } catch {
      // Partial deletes already landed; the refresh shows the real state.
      rt.refresh();
    } finally {
      setBulkBusy(false);
    }
  };

  function runAction(a: RowAction, row: Record<string, unknown>) {
    if (!rt) return;
    const rowId = String(row.id ?? "");
    if (a.action === "select") {
      selectRow(row);
      return;
    }
    if (a.action === "delete") {
      const tableId = q?.table_id;
      if (!tableId || !rowId) return;
      if (!window.confirm("Delete this record?")) return;
      portalApi
        .deleteRow(rt.appSlug, tableId, rowId)
        .then(rt.refresh)
        .catch(() => {});
      return;
    }
    // open / edit → go to the target detail page at `/p/<page>/<rowId>` (readable path)
    const target = a.target_page;
    if (!target) return;
    if ((a.open_mode ?? "drawer") === "drawer") {
      rt.openDrawer(target, { row: rowId });
    } else {
      rt.navigate(portalRowPath(rt.appSlug, target, rowId));
    }
  }

  // ── Grid (e-commerce card) mode ────────────────────────────────────────
  // Reuses ALL the server-side machinery above (search / sort / pagination /
  // row actions) — only the row RENDER differs. The whole card runs the first
  // open/edit action (or selects the row); the thumbnail is an image-URL column
  // when set, else a deterministic gradient placeholder from the title.
  const gridMode = String(node.props.layout ?? "table") === "grid";
  const cardTitleCol = String(node.props.card_title ?? "").trim() || columns[0] || "";
  const cardSubtitleCol = String(node.props.card_subtitle ?? "").trim();
  const cardImageCol = String(node.props.card_image ?? "").trim();
  const cardOpenAction =
    actions.find((a) => a.action === "open" || a.action === "edit") ??
    actions.find((a) => a.action !== "delete");
  const cardSelects =
    selectInto !== "" ||
    (Array.isArray(node.props.on_row_select) && node.props.on_row_select.length > 0);
  const cardClickable = !!cardOpenAction || (cardSelects && selectionMode === "none");
  const cardClick = (row: Record<string, unknown>) => {
    if (cardOpenAction) runAction(cardOpenAction, row);
    else if (cardSelects && selectionMode === "none") selectRow(row);
  };
  return (
    <div className="flex flex-col gap-3">
      {/* Asana-style toolbar: one dense row — search left, actions right.
          Only renders when there's something to show. */}
      {(searchable || ((colMenuEnabled || exportable) && rt)) && (
        <div className="flex items-center justify-between gap-3">
          {searchable ? (
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={search}
                placeholder="Search…"
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-[var(--portal-radius)] border border-border bg-surface py-1.5 pl-8 pr-3 text-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>
          ) : (
            <span />
          )}
          {(colMenuEnabled || exportable) && rt && (
            <div className="flex items-center gap-2">
              {exportable && (
                <button
                  type="button"
                  disabled={exporting}
                  onClick={exportCsv}
                  className="inline-flex items-center gap-1.5 rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:opacity-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  {exporting ? "Exporting…" : "Export CSV"}
                </button>
              )}
              {colMenuEnabled && (
                <ColumnManager
                  columns={orderedAll}
                  hidden={hiddenSet}
                  pinned={colPrefs.pinned}
                  canHide={canHide}
                  canPin={canPin}
                  canReorder={canReorder}
                  onToggleHidden={toggleHidden}
                  onCyclePin={cyclePin}
                  onReorder={(next) => setColPrefs((p) => ({ ...p, order: next }))}
                  onReset={() => setColPrefs(adminDefaults())}
                  views={rt ? views : undefined}
                  onSaveView={
                    rt
                      ? (name) => setViews((vs) => upsertView(vs, { name, prefs: colPrefs }))
                      : undefined
                  }
                  onApplyView={rt ? (v) => setColPrefs(v.prefs) : undefined}
                  onDeleteView={
                    rt ? (name) => setViews((vs) => vs.filter((v) => v.name !== name)) : undefined
                  }
                />
              )}
            </div>
          )}
        </div>
      )}
      {filterable && rt && Object.keys(colFilters).length > 0 && (
        <button
          type="button"
          onClick={() => setColFilters({})}
          className="self-start text-xs font-medium text-accent hover:underline"
        >
          Clear filters ({Object.keys(colFilters).length})
        </button>
      )}
      {/* Data grid 2a — multi-select bar (shows once rows are picked). The
          selection is mirrored into `select_into`, so a button can run a bulk
          action over it. */}
      {selectionMode === "multiple" && selIds.size > 0 && (
        <div className="mb-2 flex items-center gap-3 rounded-[var(--portal-radius)] border border-accent/40 bg-accent/5 px-3 py-1.5 text-sm">
          <span className="font-medium">{selIds.size} selected</span>
          {bulkAllowed && rt && (
            <button
              type="button"
              className="font-medium text-danger hover:underline disabled:opacity-50"
              disabled={bulkBusy}
              onClick={bulkDelete}
            >
              {bulkBusy ? "Deleting…" : "Delete selected"}
            </button>
          )}
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => applySelection(new Set(), visible)}
          >
            Clear
          </button>
        </div>
      )}
      {/* Grid (card) mode — an e-commerce-style responsive card grid that
          reflows by min card width; the whole card is the open/select action. */}
      {gridMode && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
        >
          {visible.map((row, i) => {
            const rid = String(row.id ?? i);
            const title = cellText(relValue(row, cardTitleCol));
            const subtitle = cardSubtitleCol ? cellText(relValue(row, cardSubtitleCol)) : "";
            const imgVal = cardImageCol ? relValue(row, cardImageCol) : null;
            const isSel =
              selectionMode === "none" &&
              selectInto !== "" &&
              rid === selectedId &&
              selectedId !== "";
            const chips = columns
              .filter((c) => c !== cardTitleCol && c !== cardSubtitleCol)
              .slice(0, 2);
            return (
              <button
                key={rid}
                type="button"
                onClick={cardClickable ? () => cardClick(row) : undefined}
                className={`group flex flex-col overflow-hidden rounded-[var(--portal-radius)] border bg-surface text-left shadow-sm transition ${
                  cardClickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : ""
                } ${isSel ? "border-accent ring-1 ring-accent" : "border-border"}`}
              >
                <div className="relative aspect-[4/3] w-full">
                  <CardThumb appSlug={appSlug} value={imgVal} alt={title} />
                </div>
                <div className="flex flex-1 flex-col gap-1 p-3">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {title || "—"}
                  </div>
                  {subtitle && (
                    <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
                  )}
                  {chips.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {chips.map((c) => (
                        <span
                          key={c}
                          className="truncate rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground"
                        >
                          {cellText(relValue(row, c))}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {/* Asana-style list: borderless + full-bleed (no card/shadow), just a
          header hairline + faint row dividers; a wide table scrolls
          horizontally instead of bleeding past the page width. */}
      {!gridMode && (
        <div className="w-full max-w-full overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {selectionMode !== "none" && (
                  <th className={`w-10 px-3 ${padY}`}>
                    {selectionMode === "multiple" && (
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        disabled={!rt}
                        checked={
                          pageSelectionState(
                            visible.map((r) => String(r.id ?? "")),
                            selIds,
                          ) === "all"
                        }
                        ref={(el) => {
                          if (el)
                            el.indeterminate =
                              pageSelectionState(
                                visible.map((r) => String(r.id ?? "")),
                                selIds,
                              ) === "some";
                        }}
                        onChange={() =>
                          applySelection(
                            togglePage(
                              visible.map((r) => String(r.id ?? "")),
                              selIds,
                            ),
                            visible,
                          )
                        }
                      />
                    )}
                  </th>
                )}
                {columns.map((c) => {
                  const active = sortCol === c;
                  const pin = pinProps(c);
                  return (
                    <th
                      key={c}
                      style={pin.style}
                      className={`whitespace-nowrap px-4 py-2 text-xs font-medium text-muted-foreground ${
                        pinOf(c) ? "" : "relative"
                      } ${pin.className}`}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        {sortable ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(c)}
                            className="group inline-flex items-center gap-1 transition-colors hover:text-foreground"
                          >
                            {humanizeColumn(c)}
                            {active ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3 text-foreground" strokeWidth={2.5} />
                              ) : (
                                <ChevronDown
                                  className="h-3 w-3 text-foreground"
                                  strokeWidth={2.5}
                                />
                              )
                            ) : (
                              <ChevronsUpDown
                                className="h-3 w-3 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground"
                                strokeWidth={2}
                              />
                            )}
                          </button>
                        ) : (
                          humanizeColumn(c)
                        )}
                        {rt && filterable && (columnConfig[c]?.filter ?? "text") !== "none" && (
                          <ColumnFilter
                            column={c}
                            mode={
                              relFilterOpts[c]
                                ? "relation"
                                : ((columnConfig[c]?.filter as
                                    | "text"
                                    | "select"
                                    | "range"
                                    | "daterange") ?? "text")
                            }
                            options={columnConfig[c]?.options ?? []}
                            relationOptions={relFilterOpts[c]}
                            value={colFilters[c] ?? null}
                            onChange={(f) =>
                              setColFilters((m) => {
                                const next = { ...m };
                                if (f) next[c] = f;
                                else delete next[c];
                                return next;
                              })
                            }
                          />
                        )}
                      </div>
                      {canResize && rt && (
                        <span
                          aria-hidden
                          onMouseDown={(e) => startResize(e, c)}
                          className="absolute right-0 top-0 z-20 h-full w-1.5 cursor-col-resize select-none hover:bg-accent/40"
                        />
                      )}
                    </th>
                  );
                })}
                {actions.length > 0 && (
                  <th style={actionsPin.style} className={`px-4 ${padY}${actionsPin.className}`} />
                )}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (actions.length > 0 ? 1 : 0) +
                      (selectionMode !== "none" ? 1 : 0)
                    }
                    className="px-4 py-10"
                  >
                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <SearchX className="h-7 w-7 opacity-40" strokeWidth={1.5} />
                      <span className="text-sm">No matching rows</span>
                    </div>
                  </td>
                </tr>
              )}
              {visible.map((row, i) => {
                const rid = String(row.id ?? "");
                // Rows are click-selectable when EITHER `select_into` (persist the row)
                // OR an `on_row_select` handler (F4.2) is set — so the event handler
                // works standalone without binding a page var.
                const hasRowSelectHandler =
                  Array.isArray(node.props.on_row_select) && node.props.on_row_select.length > 0;
                const rowClickSelect =
                  selectionMode === "none" && (selectInto !== "" || hasRowSelectHandler) && !!rt;
                // Touch-friendly: when the row has a primary open/edit action, TAPPING
                // the whole row runs it (the hover action buttons don't reveal on
                // touch). Mirrors the grid card. `cardClick` prefers open, else select.
                const rowOpens = selectionMode === "none" && !!cardOpenAction && !!rt;
                const rowClickable = rowOpens || rowClickSelect;
                const isSelected =
                  selectionMode === "none"
                    ? selectInto !== "" && rid === selectedId && selectedId !== ""
                    : selIds.has(rid);
                return (
                  <tr
                    key={String(row.id ?? i)}
                    className={`group border-b border-border/50 transition-colors last:border-0 ${
                      rowClickable ? "cursor-pointer" : ""
                    } ${isSelected ? "bg-accent/[0.07]" : "hover:bg-muted/40"}`}
                    onClick={rowClickable ? () => cardClick(row) : undefined}
                  >
                    {selectionMode !== "none" && (
                      <td
                        className={`w-10 px-3 ${padY} align-middle`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type={selectionMode === "single" ? "radio" : "checkbox"}
                          aria-label="Select row"
                          disabled={!rt || rid === ""}
                          checked={selIds.has(rid)}
                          onChange={() =>
                            applySelection(
                              selectionMode === "single"
                                ? new Set([rid])
                                : toggleSelected(selIds, rid),
                              visible,
                            )
                          }
                        />
                      </td>
                    )}
                    {columns.map((c, ci) => {
                      const pin = pinProps(c);
                      const rowId = String(row.id ?? "");
                      const editable = inlineEdit && !!rt && !!editFields[c] && rowId !== "";
                      const isEditing = editCell?.col === c && editCell?.rowId === rowId;
                      // Asana-style: the first (leftmost) column reads as the row's
                      // "name" — slightly stronger weight + full foreground colour.
                      const emphasize = ci === 0;
                      return (
                        <td
                          key={c}
                          style={pin.style}
                          className={`px-4 ${padY} align-middle ${
                            emphasize ? "font-medium text-foreground" : ""
                          } ${editable ? "cursor-text" : ""} ${pin.className}`}
                          title={isEditing ? undefined : formatCell(relValue(row, c))}
                          onDoubleClick={
                            editable && !isEditing
                              ? () => setEditCell({ col: c, rowId })
                              : undefined
                          }
                          // While editing, keep clicks inside the cell from selecting the row.
                          onClick={isEditing ? (e) => e.stopPropagation() : undefined}
                        >
                          {isEditing ? (
                            <InlineCellEditor
                              field={editFields[c]}
                              initial={row[c]}
                              busy={editBusy}
                              onCommit={(v) => commitEdit(c, rowId, v)}
                              onCancel={() => setEditCell(null)}
                            />
                          ) : (
                            // Truncation lives on an INNER div, never the <td>: `truncate`
                            // (overflow-hidden + nowrap) on a border-collapse auto-layout
                            // cell collapses the whole COLUMN to the ellipsis min-width
                            // (the "first column gets clipped" bug). The td now sizes
                            // naturally; the div caps the content at 280px.
                            <div className="max-w-[280px] truncate">
                              {renderCellValue(relValue(row, c))}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    {actions.length > 0 && (
                      <td
                        style={actionsPin.style}
                        className={`px-4 ${padY} text-right${actionsPin.className}`}
                      >
                        {/* Asana-style: row actions stay quiet, revealing on row
                    hover / keyboard focus (always visible on touch via
                    focus-within fallback). Non-delete actions use the brand
                    accent (`--portal-accent`), delete the semantic danger. */}
                        <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity duration-100 group-hover:opacity-100 focus-within:opacity-100">
                          {actions.map((a, ai) => (
                            <button
                              key={ai}
                              type="button"
                              disabled={!rt}
                              // stop the row-select click when an action button is hit
                              onClick={(e) => {
                                e.stopPropagation();
                                runAction(a, row);
                              }}
                              className={`rounded-[var(--portal-radius)] px-2 py-1 text-xs font-medium transition disabled:opacity-50 ${
                                a.action === "delete"
                                  ? "text-danger hover:bg-danger/10"
                                  : "text-[var(--portal-accent,hsl(var(--accent)))] hover:bg-muted/60"
                              }`}
                            >
                              {a.label || a.action || "Open"}
                            </button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {pageSize > 0 && (
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>
              {effTotal === 0
                ? "0 of 0"
                : `${(curPage - 1) * pageSize + 1}–${Math.min(curPage * pageSize, effTotal)} of ${effTotal}`}
            </span>
            {/* G9: runtime rows-per-page selector (persisted per-user). rt-only,
                like the other live-data controls. */}
            {rt && (
              <label className="flex items-center gap-1">
                <span className="sr-only">Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-[var(--portal-radius)] border border-border bg-surface px-1.5 py-1 text-xs shadow-sm outline-none transition hover:bg-accent/5 focus:border-accent focus:ring-2 focus:ring-accent/20"
                >
                  {pageSizeOptions(adminPageSize).map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={curPage <= 1}
                onClick={() => setPage(curPage - 1)}
                className="rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-1 font-medium shadow-sm transition hover:bg-accent/5 disabled:opacity-40 disabled:hover:bg-surface"
              >
                Prev
              </button>
              <span className="px-1.5 tabular-nums">
                {curPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={curPage >= totalPages}
                onClick={() => setPage(curPage + 1)}
                className="rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-1 font-medium shadow-sm transition hover:bg-accent/5 disabled:opacity-40 disabled:hover:bg-surface"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Input widgets (S13) bound to page state. Each writes its value into
 * `$page.<state_key>` via the runtime, so a button's server action can read
 * `{{ $page.<state_key> }}`. Seeds the state from `default` on mount (so the
 * binding is defined before the user types). Inert in the builder preview
 * (no runtime) — rendered disabled, showing the default, so the layout reads.
 */
function PortalInput({
  node,
  scope,
  rt,
  builder,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
  /** True in the WYSIWYG builder: render the input non-interactive
   *  (`pointer-events-none`) instead of `disabled`, so it isn't greyed out AND
   *  a click still selects the node (a disabled control swallows the click). */
  builder?: boolean;
}): ReactNode {
  const type = node.type;
  const key = String(node.props.state_key ?? "").trim();
  const label = str(node.props.label, scope);
  const placeholder = str(node.props.placeholder, scope);
  const rawDefault = tmpl(node.props.default, scope);
  const options = Array.isArray(node.props.options)
    ? (node.props.options as unknown[]).map(String)
    : [];

  // The initial state value for this input, by widget type.
  const initial =
    type === "checkbox_input"
      ? node.props.default === true
      : type === "number_input"
        ? rawDefault === "" || rawDefault == null
          ? null
          : Number(rawDefault)
        : rawDefault == null
          ? ""
          : String(rawDefault);

  // Seed page state once (so {{ $page.key }} is defined before interaction).
  useEffect(() => {
    if (rt && key && rt.vars[key] === undefined) rt.setVar(key, initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt, key]);

  const stateVal = rt && key ? rt.vars[key] : undefined;
  // F4.2 per-widget events: `set` writes state (if bound) THEN fires `on_change`
  // with `$event.value`; `blur` fires `on_blur`. The chain runs even with no
  // `state_key` (an input can have a handler without persisting to `$page`).
  //
  // DEBOUNCE (the "fires per keystroke" fix): the STATE write is always immediate
  // (so `{{ $page.key }}` bindings stay reactive as you type), but the `on_change`
  // ACTION CHAIN is debounced — a server action shouldn't fire on every keystroke.
  // The window is the `debounce` prop (ms); typeable inputs default to 300, the
  // discrete ones (select/checkbox) to 0 (a single change per pick — nothing to
  // debounce). `on_blur` is the immediate "commit", and on blur we FLUSH any
  // pending `on_change` first (sane order: change → blur, no late fire).
  const isTypeable = type === "text_input" || type === "number_input" || type === "textarea_input";
  const debounceMs =
    node.props.debounce != null ? Number(node.props.debounce) : isTypeable ? 300 : 0;
  const changeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(changeTimer.current), []);
  const set = (v: unknown) => {
    if (rt && key) rt.setVar(key, v);
    if (debounceMs > 0) {
      clearTimeout(changeTimer.current);
      changeTimer.current = setTimeout(() => {
        changeTimer.current = undefined;
        fireEvent(node.props.on_change, rt, scope, { value: v });
      }, debounceMs);
    } else {
      fireEvent(node.props.on_change, rt, scope, { value: v });
    }
  };
  const blur = (v: unknown) => {
    if (changeTimer.current) {
      clearTimeout(changeTimer.current);
      changeTimer.current = undefined;
      fireEvent(node.props.on_change, rt, scope, { value: v });
    }
    fireEvent(node.props.on_blur, rt, scope, { value: v });
  };
  // In the builder we DON'T disable (a disabled control swallows the click so
  // you can't select the node) — instead make it `pointer-events-none` so the
  // click reaches the EditNode wrapper and selects the input. Truly inert only
  // if there's neither a runtime nor the builder (shouldn't happen).
  const disabled = !rt && !builder;
  const pe = builder ? "pointer-events-none" : "";
  const control = `w-full rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-2 text-sm shadow-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-60 ${pe}`;

  let field: ReactNode;
  if (type === "checkbox_input") {
    const checked = stateVal === undefined ? Boolean(initial) : Boolean(stateVal);
    return (
      <label className={`flex items-center gap-2 text-sm ${pe}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => set(e.target.checked)}
          className="h-4 w-4 rounded border-border text-accent accent-accent"
        />
        {label || key}
      </label>
    );
  } else if (type === "select_input") {
    field = (
      <select
        className={control}
        disabled={disabled}
        value={String(stateVal ?? initial ?? "")}
        onChange={(e) => set(e.target.value)}
        onBlur={(e) => blur(e.target.value)}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  } else if (type === "textarea_input") {
    field = (
      <textarea
        className={control}
        rows={3}
        disabled={disabled}
        placeholder={placeholder}
        value={String(stateVal ?? initial ?? "")}
        onChange={(e) => set(e.target.value)}
        onBlur={(e) => blur(e.target.value)}
      />
    );
  } else if (type === "number_input") {
    field = (
      <input
        type="number"
        className={control}
        disabled={disabled}
        placeholder={placeholder}
        value={stateVal == null ? "" : Number(stateVal)}
        onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))}
        onBlur={(e) => blur(e.target.value === "" ? null : Number(e.target.value))}
      />
    );
  } else {
    // text_input (default)
    field = (
      <input
        className={control}
        disabled={disabled}
        placeholder={placeholder}
        value={String(stateVal ?? initial ?? "")}
        onChange={(e) => set(e.target.value)}
        onBlur={(e) => blur(e.target.value)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-sm font-medium text-foreground">{label}</label>}
      {field}
    </div>
  );
}

/** Editing context: when present, every rendered node is wrapped in a
 *  selectable overlay (WYSIWYG builder). Undefined in the portal runtime —
 *  zero overhead, identical output. */
export interface EditCtx {
  selectedId: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  /**
   * Drop handler with a precise insertion point (Elementor-style). `payload`
   * is either an existing node id (reorder) or `new:<type>` (a component
   * dragged from the palette). `position` says where, relative to `targetId`:
   * `before` / `after` = sibling, `inside` = append into the target container.
   */
  onDrop: (payload: string, targetId: string, position: DropPos) => void;
  /** Whether a node type accepts children (drives the `inside` drop zone). */
  allowsChildren: (type: string) => boolean;
  /** Whether a node sits in a HORIZONTAL container (e.g. `columns`) — then
   *  drop before/after is computed on the X axis and the insertion line is
   *  vertical (left/right edge) instead of horizontal (top/bottom). */
  horizontalAt: (id: string) => boolean;
  /** Friendly label for a node type (from the catalog). */
  labelFor: (type: string) => string;
  /** Active viewport-preview breakpoint (S15b). Drives the GREY-OUT of nodes
   *  whose `hide_*` token would hide them at this width on the live portal —
   *  the preview is a width box, not a real media context, so we can't rely on
   *  the media-query class. Undefined → no responsive dimming. */
  previewBreakpoint?: Breakpoint;
}

export type DropPos = "before" | "after" | "inside" | "left" | "right";

/** Curated lucide set offered by the `stat` component's `icon` enum (mirrors
 *  the catalog `props_schema`). Fixed map, not a dynamic lookup, so the bundle
 *  only pulls these. `none` (and any unknown value) → no leading icon. */
const STAT_ICONS: Record<string, LucideIcon> = {
  activity: Activity,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  "dollar-sign": DollarSign,
  users: Users,
  "shopping-cart": ShoppingCart,
  package: Package,
  "bar-chart": BarChart3,
  clock: Clock,
  "check-circle": CheckCircle2,
  "alert-triangle": AlertTriangle,
};

/** Tone vocabulary shared by `callout` (banner) and `badge` (pill). Each tone
 *  maps to theme-token classes + (for callouts) a lucide icon. `neutral` is
 *  badge-only. Keep the keys in sync with the catalog `tone` enums. */
const CALLOUT_TONES: Record<string, { box: string; icon: LucideIcon; iconCls: string }> = {
  info: {
    box: "border-accent/30 bg-accent/10 text-foreground",
    icon: Info,
    iconCls: "text-accent",
  },
  success: {
    box: "border-success/30 bg-success/10 text-foreground",
    icon: CheckCircle2,
    iconCls: "text-success",
  },
  warning: {
    box: "border-warning/30 bg-warning/10 text-foreground",
    icon: AlertTriangle,
    iconCls: "text-warning",
  },
  danger: {
    box: "border-danger/30 bg-danger/10 text-foreground",
    icon: AlertCircle,
    iconCls: "text-danger",
  },
};

const BADGE_TONES: Record<string, string> = {
  neutral: "bg-muted text-muted-foreground",
  info: "bg-accent/15 text-accent",
  success: "bg-success/15 text-success",
  warning: "bg-warning/15 text-warning",
  danger: "bg-danger/15 text-danger",
};

/** Fill colour of the `progress` bar's track (#6d). Keys mirror the catalog
 *  `tone` enum. */
const PROGRESS_TONES: Record<string, string> = {
  accent: "bg-accent",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

/** Text colour of the `icon` component (#6d). Keys mirror the catalog `tone`
 *  enum; `default` inherits the surrounding text colour. */
const ICON_TONES: Record<string, string> = {
  default: "",
  muted: "text-muted-foreground",
  accent: "text-accent",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

/** Pixel size of the `icon` component (#6d), mirroring the catalog `size`
 *  enum. */
const ICON_SIZES: Record<string, string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-7 w-7",
  xl: "h-10 w-10",
};

/** Curated lucide set offered by the `icon` component's `name` enum (#6d).
 *  Fixed map (not a dynamic lookup) so the bundle only pulls these. */
const ICON_SET: Record<string, LucideIcon> = {
  "check-circle": CheckCircle2,
  "alert-triangle": AlertTriangle,
  info: Info,
  clock: Clock,
  star: Star,
  heart: Heart,
  zap: Zap,
  bell: Bell,
  flag: Flag,
  shield: Shield,
  user: User,
  package: Package,
};

/** Dashboard-wide filter control (3b-2): a select / text / date-range bound to
 *  a column. Changing it sets the page GLOBAL filter for THIS column (preserving
 *  filters other `page_filter`s own), so every block re-queries scoped to it.
 *  Native `<select>`/`<input>` are fine in the portal runtime (the no-native-
 *  select rule is inspector-only). Runtime-only: inert in the builder preview. */
function PortalPageFilter({
  node,
  rt,
  builder,
}: {
  node: PageNode;
  rt: PageRuntime | null;
  builder: boolean;
}): ReactNode {
  const column = String(node.props.column ?? "").trim();
  const control = String(node.props.control ?? "select");
  const label = String(node.props.label ?? column);
  const options = String(node.props.options ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const active = (rt?.globalFilters ?? []).filter((f) => f.column === column);
  // Replace THIS column's filters, keep the others (multiple page_filters compose).
  const apply = (mine: GlobalFilter[]) => {
    if (!rt || !column) return;
    const others = rt.globalFilters.filter((f) => f.column !== column);
    rt.setGlobalFilter([...others, ...mine]);
  };

  const wrap = (inner: ReactNode) => (
    <div key={node.id} className={builder ? "pointer-events-none" : undefined}>
      {label && (
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      )}
      {inner}
    </div>
  );
  const cls =
    "w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-foreground";

  if (control === "daterange") {
    const from = String(active.find((f) => f.op === "gte")?.value ?? "");
    const to = String(active.find((f) => f.op === "lte")?.value ?? "");
    const build = (lo: string, hi: string) => {
      const out: GlobalFilter[] = [];
      if (lo) out.push({ column, op: "gte", value: lo, label });
      if (hi) out.push({ column, op: "lte", value: hi, label });
      apply(out);
    };
    return wrap(
      <div className="flex items-center gap-2">
        <input
          type="date"
          className={cls}
          value={from}
          onChange={(e) => build(e.target.value, to)}
        />
        <span className="text-xs text-muted-foreground">→</span>
        <input
          type="date"
          className={cls}
          value={to}
          onChange={(e) => build(from, e.target.value)}
        />
      </div>,
    );
  }

  if (control === "text") {
    const val = String(active.find((f) => f.op === "contains")?.value ?? "");
    return wrap(
      <input
        className={cls}
        placeholder={`Filtra ${column}…`}
        value={val}
        onChange={(e) =>
          apply(e.target.value ? [{ column, op: "contains", value: e.target.value, label }] : [])
        }
      />,
    );
  }

  // select (default)
  const val = String(active.find((f) => f.op === "eq")?.value ?? "");
  return wrap(
    <select
      className={cls}
      value={val}
      onChange={(e) =>
        apply(e.target.value ? [{ column, op: "eq", value: e.target.value, label }] : [])
      }
    >
      <option value="">Tutti</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>,
  );
}

/** Parse a `progress` value/max into a clamped 0–100 percentage. Non-numeric
 *  or a max ≤ 0 → 0, so a junk binding renders an empty (not broken) bar. */
function progressPct(value: string, max: string): number {
  const v = Number(value);
  const m = Number(max) || 100;
  if (!Number.isFinite(v) || !Number.isFinite(m) || m <= 0) return 0;
  return Math.max(0, Math.min(100, (v / m) * 100));
}

/** `chart` tone → the CSS custom property holding its colour (resolved at
 *  runtime so app branding / the portal light theme are respected). */
const CHART_TONE_VARS: Record<string, string> = {
  accent: "--accent",
  success: "--success",
  warning: "--warning",
  danger: "--danger",
};

/** Read a `--token` HSL triplet off the element's computed style (it lives
 *  under `.tray-light`, so this resolves the PORTAL theme, not the admin one)
 *  and wrap it as a CSS colour. `alpha` adds the modern `/ a` opacity slot. */
function cssToneColor(el: HTMLElement | null, varName: string, alpha = 1): string {
  const fallback: Record<string, string> = {
    "--accent": "222 47% 11%",
    "--success": "142 71% 36%",
    "--warning": "32 95% 44%",
    "--danger": "0 72% 51%",
    "--muted-foreground": "220 9% 42%",
    "--border": "220 13% 86%",
  };
  const triplet =
    (el && getComputedStyle(el).getPropertyValue(varName).trim()) ||
    fallback[varName] ||
    "0 0% 50%";
  return alpha >= 1 ? `hsl(${triplet})` : `hsl(${triplet} / ${alpha})`;
}

/** A `chart` (#6e) — bar / line / area — bound to a page query, rendered with
 *  Chart.js (`react-chartjs-2`). Reads `{x_field,y_field}` off each row of
 *  the query bound via its `data` binding; non-numeric y values are dropped. Series + axis
 *  colours resolve from the live theme tokens via the wrapper ref, so a
 *  branded portal recolours the chart for free. */
function Chart({
  node,
  scope,
  rt,
  editing,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
  editing?: EditCtx;
}) {
  const ref = effectiveQueryRef(node.props);
  const kind = String(node.props.kind ?? "bar");
  const xField = String(node.props.x_field ?? "");
  const yField = String(node.props.y_field ?? "");
  const title = str(node.props.title, scope);
  const toneVar = CHART_TONE_VARS[String(node.props.tone ?? "accent")] ?? CHART_TONE_VARS.accent;

  const boxRef = useRef<HTMLDivElement>(null);
  // Resolve theme colours after mount (computed styles need a live DOM node).
  // Slice colours for pie/doughnut + stacked series — distinct theme hues, then
  // lighter repeats so a many-slice chart stays legible.
  const sliceVars: [string, number][] = [
    ["--accent", 1],
    ["--success", 1],
    ["--warning", 1],
    ["--danger", 1],
    ["--accent", 0.55],
    ["--success", 0.55],
    ["--warning", 0.55],
    ["--danger", 0.55],
  ];
  const buildPalette = (el: HTMLElement | null) => ({
    series: cssToneColor(el, toneVar),
    fill: cssToneColor(el, toneVar, 0.18),
    muted: cssToneColor(el, "--muted-foreground"),
    grid: cssToneColor(el, "--border"),
    slices: sliceVars.map(([v, a]) => cssToneColor(el, v, a)),
  });
  const [palette, setPalette] = useState(() => buildPalette(null));
  useLayoutEffect(() => {
    setPalette(buildPalette(boxRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toneVar]);

  const q = (scope.$queries as Record<string, { rows: Record<string, unknown>[] }>)[ref];
  const rows = ref ? (q?.rows ?? []) : arbitraryDataRows(node, scope);
  const points = rows
    .map((r) => ({ label: String(r[xField] ?? ""), value: Number(r[yField]) }))
    .filter((p) => Number.isFinite(p.value));

  if (points.length === 0) {
    const msg = editing
      ? ref
        ? "Chart — no rows / pick x & y columns"
        : "Chart — bind a query"
      : "No data";
    return (
      <div
        key={node.id}
        ref={boxRef}
        className="flex h-48 w-full items-center justify-center rounded-[var(--portal-radius)] border border-dashed border-border text-xs text-muted-foreground/70"
      >
        {msg}
      </div>
    );
  }

  const isPie = kind === "pie" || kind === "doughnut";
  const seriesField = String(node.props.series_field ?? "");
  const isStacked = kind === "stacked" && seriesField !== "";

  let data;
  if (isPie) {
    // One slice per row; colours cycle through the theme palette.
    data = {
      labels: points.map((p) => p.label),
      datasets: [
        {
          data: points.map((p) => p.value),
          backgroundColor: points.map((_, i) => palette.slices[i % palette.slices.length]),
          borderColor: palette.grid,
          borderWidth: 1,
        },
      ],
    };
  } else if (isStacked) {
    // Pivot rows into one dataset per `series_field` value, stacked over x.
    const xs: string[] = [];
    const seriesVals: string[] = [];
    const cell = new Map<string, number>();
    for (const r of rows) {
      const x = String(r[xField] ?? "");
      const s = String(relValue(r, seriesField) ?? r[seriesField] ?? "");
      const v = Number(r[yField]);
      if (!Number.isFinite(v)) continue;
      if (!xs.includes(x)) xs.push(x);
      if (!seriesVals.includes(s)) seriesVals.push(s);
      const k = `${x} ${s}`;
      cell.set(k, (cell.get(k) ?? 0) + v);
    }
    data = {
      labels: xs,
      datasets: seriesVals.map((s, i) => ({
        label: s,
        data: xs.map((x) => cell.get(`${x} ${s}`) ?? 0),
        backgroundColor: palette.slices[i % palette.slices.length],
        borderWidth: 0,
        borderRadius: 4,
        stack: "s",
      })),
    };
  } else {
    data = {
      labels: points.map((p) => p.label),
      datasets: [
        {
          label: yField || "value",
          data: points.map((p) => p.value),
          backgroundColor: kind === "bar" ? palette.series : palette.fill,
          borderColor: palette.series,
          borderWidth: kind === "bar" ? 0 : 2,
          borderRadius: kind === "bar" ? 4 : 0,
          fill: kind === "area",
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: palette.series,
        },
      ],
    };
  }

  // Drill-down (3b): clicking a bar/slice/point sets a dashboard-wide filter on
  // the x column = the clicked category, re-scoping every block. Runtime-only
  // (rt present, not the builder); opt-in via the `drill_filter` prop.
  const drillEnabled = Boolean(node.props.drill_filter) && !!rt;
  const drillLabels = (data.labels as string[]) ?? [];
  const onDrill = drillEnabled
    ? (_e: unknown, elements: { index: number }[]) => {
        if (!elements?.length || !xField) return;
        const label = drillLabels[elements[0].index];
        if (label == null || label === "") return;
        rt!.setGlobalFilter([{ column: xField, op: "eq", value: label, label: xField }]);
      }
    : undefined;

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    onClick: onDrill,
    plugins: {
      legend: {
        display: true,
        position: "right" as const,
        labels: { color: palette.muted, font: { size: 10 } },
      },
      tooltip: { enabled: true },
    },
  };
  const axisOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    onClick: onDrill,
    plugins: {
      legend: {
        display: isStacked,
        position: "bottom" as const,
        labels: { color: palette.muted, font: { size: 10 } },
      },
      tooltip: { enabled: true },
    },
    scales: {
      x: {
        stacked: isStacked,
        grid: { display: false },
        ticks: { color: palette.muted, font: { size: 10 } },
      },
      y: {
        stacked: isStacked,
        beginAtZero: true,
        grid: { color: palette.grid },
        ticks: { color: palette.muted, font: { size: 10 } },
      },
    },
  };

  return (
    <div key={node.id} ref={boxRef} className="w-full">
      {title && <div className="mb-2 text-sm font-semibold text-foreground">{title}</div>}
      <div className="relative h-48 w-full">
        {kind === "pie" ? (
          <Pie
            data={data as ChartData<"pie", number[], string>}
            options={pieOptions as ChartOptions<"pie">}
          />
        ) : kind === "doughnut" ? (
          <Doughnut
            data={data as ChartData<"doughnut", number[], string>}
            options={pieOptions as ChartOptions<"doughnut">}
          />
        ) : kind === "line" || kind === "area" ? (
          <Line
            data={data as ChartData<"line", number[], string>}
            options={axisOptions as ChartOptions<"line">}
          />
        ) : (
          <Bar
            data={data as ChartData<"bar", number[], string>}
            options={axisOptions as ChartOptions<"bar">}
          />
        )}
      </div>
    </div>
  );
}

/** Kanban board — the bound query's rows grouped into columns by `group_field`
 *  (a select/relation column). Cards show title/subtitle/badge + optional
 *  per-card actions. At RUNTIME (rt present), dragging a card to another column
 *  PATCHes its `group_field` to the target value (write-policy gated server-side)
 *  then refreshes. In the BUILDER (rt null) it's a read-only preview over the
 *  sample rows. Columns are derived from the group values present, first-seen
 *  order; a relation group shows the resolved label (`__rel`) as the header. */
function Kanban({
  node,
  scope,
  rt,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
}): ReactNode {
  const ref = effectiveQueryRef(node.props);
  const groupField = String(node.props.group_field ?? "");
  const titleField = String(node.props.title_field ?? "");
  const subtitleField = String(node.props.subtitle_field ?? "");
  const badgeField = String(node.props.badge_field ?? "");
  const movable = node.props.movable !== false;
  const searchable = node.props.searchable === true;
  const selectInto = String(node.props.select_into ?? "").trim();
  const actions = (node.props.row_actions as RowAction[] | undefined) ?? [];

  const [search, setSearch] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);

  const q = (
    scope.$queries as Record<string, { rows: Record<string, unknown>[]; table_id?: string }>
  )[ref];
  const allRows = ref ? (q?.rows ?? []) : arbitraryDataRows(node, scope);

  // Unbound → a builder placeholder (mirrors the chart's "bind a query" hint).
  if (!ref || !groupField) {
    return (
      <div
        key={node.id}
        className="flex h-40 w-full items-center justify-center rounded-[var(--portal-radius)] border border-dashed border-border text-xs text-muted-foreground/70"
      >
        Kanban — bind a query + group column
      </div>
    );
  }

  const rows =
    searchable && search.trim()
      ? allRows.filter((r) =>
          Object.values(r).some((v) =>
            String(v ?? "")
              .toLowerCase()
              .includes(search.trim().toLowerCase()),
          ),
        )
      : allRows;

  // Group rows by the raw value of `group_field` (the id for relations), keeping
  // first-seen order; remember each group's display label (relation → __rel).
  const order: string[] = [];
  const byGroup = new Map<string, Record<string, unknown>[]>();
  const labelOf = new Map<string, string>();
  for (const row of rows) {
    const key = String(row[groupField] ?? "");
    if (!byGroup.has(key)) {
      byGroup.set(key, []);
      order.push(key);
      const lbl = String(relValue(row, groupField) ?? "").trim();
      labelOf.set(key, lbl || "—");
    }
    byGroup.get(key)!.push(row);
  }

  const selectedId =
    selectInto && rt
      ? String((rt.vars[selectInto] as Record<string, unknown> | undefined)?.id ?? "")
      : "";

  const moveCard = (rowId: string, toKey: string) => {
    const tableId = q?.table_id;
    if (!rt || !movable || !tableId || !rowId) return;
    portalApi
      .updateRow(rt.appSlug, tableId, rowId, { [groupField]: toKey })
      .then(rt.refresh)
      .catch(() => {});
  };

  function runAction(a: RowAction, row: Record<string, unknown>) {
    if (!rt) return;
    const rowId = String(row.id ?? "");
    if (a.action === "select") {
      if (selectInto) rt.setVar(selectInto, row);
      return;
    }
    if (a.action === "delete") {
      const tableId = q?.table_id;
      if (!tableId || !rowId) return;
      if (!window.confirm("Delete this record?")) return;
      portalApi
        .deleteRow(rt.appSlug, tableId, rowId)
        .then(rt.refresh)
        .catch(() => {});
      return;
    }
    const target = a.target_page;
    if (!target) return;
    if ((a.open_mode ?? "drawer") === "drawer") rt.openDrawer(target, { row: rowId });
    else rt.navigate(portalRowPath(rt.appSlug, target, rowId));
  }

  const draggable = movable && !!rt;

  return (
    <div key={node.id} className="flex flex-col gap-3">
      {searchable && (
        <div className="relative w-full max-w-xs self-start">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            placeholder="Search…"
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-[var(--portal-radius)] border border-border bg-surface py-2 pl-8 pr-3 text-sm shadow-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </div>
      )}
      {order.length === 0 ? (
        <div className="rounded-[var(--portal-radius)] border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {str(node.props.empty_text, scope) || "No cards"}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {order.map((key) => (
            <div
              key={key}
              data-kanban-column={key}
              onDragOver={draggable ? (e) => e.preventDefault() : undefined}
              onDrop={
                draggable
                  ? (e) => {
                      e.preventDefault();
                      if (dragId) moveCard(dragId, key);
                      setDragId(null);
                    }
                  : undefined
              }
              className="flex w-64 shrink-0 flex-col gap-2 rounded-[var(--portal-radius)] bg-muted/30 p-2"
            >
              <div className="flex items-center justify-between px-1 pt-0.5">
                <span className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {labelOf.get(key)}
                </span>
                <span className="ml-1 shrink-0 rounded-full bg-muted px-1.5 text-[11px] text-muted-foreground">
                  {byGroup.get(key)!.length}
                </span>
              </div>
              {byGroup.get(key)!.map((row, ri) => {
                const rowId = String(row.id ?? ri);
                const selected = selectedId && rowId === selectedId;
                return (
                  <div
                    key={rowId}
                    draggable={draggable}
                    onDragStart={draggable ? () => setDragId(rowId) : undefined}
                    onClick={selectInto && rt ? () => rt.setVar(selectInto, row) : undefined}
                    className={[
                      "rounded-md border bg-surface p-2 shadow-sm transition",
                      selected ? "border-accent ring-1 ring-accent/30" : "border-border",
                      selectInto && rt ? "cursor-pointer hover:border-accent" : "",
                      draggable ? "cursor-grab active:cursor-grabbing" : "",
                    ]
                      .join(" ")
                      .replace(/\s+/g, " ")
                      .trim()}
                  >
                    <div className="text-sm font-medium text-foreground">
                      {String(relValue(row, titleField) ?? "") || "—"}
                    </div>
                    {subtitleField && (
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {String(relValue(row, subtitleField) ?? "")}
                      </div>
                    )}
                    {badgeField && (
                      <div className="mt-1.5">{renderCellValue(relValue(row, badgeField))}</div>
                    )}
                    {actions.length > 0 && rt && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {actions.map((a, ai) => (
                          <button
                            key={ai}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              runAction(a, row);
                            }}
                            className="rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
                          >
                            {a.label || a.action || "Action"}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Related list (sub-grid) — lists CHILD rows of a related table where the
 *  child's `relation_field` equals `parent_id` (default the open record's id,
 *  `{{ $page.row }}`). The relation keystone on the interface side: drop one
 *  inside a record page to show e.g. a company's deals. Fetches client-side via
 *  the RLS-gated `portalApi.queryRows` (so it respects the role's row policy).
 *  Read-only placeholder in the builder (no runtime / no parent id). */
/** Comment timeline on the open record (D21). Lists a row's comments
 *  newest-first + a composer; a member may delete only their own. Fetches
 *  client-side via the portal endpoints (member + read-RLS gated server-side).
 *  In the builder (no `rt`) / unbound → a read-only placeholder. */
function ActivityPanel({
  node,
  scope,
  rt,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
}): ReactNode {
  const tableId = String(node.props.table_id ?? "");
  const rawRow = String(node.props.row_id ?? "").trim() || "{{ $page.row }}";
  const rowId = str(rawRow, scope).trim();
  const title = str(node.props.title, scope);
  const appSlug = rt?.appSlug ?? "";
  const me = scope.$user as PortalUser | null;
  const myPrincipal = me ? `app_user:${me.id}` : null;

  const [comments, setComments] = useState<PortalComment[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // @mention autocomplete (ADR-0020 P3b): the app's members + the active
  // `@query` (the partial name being typed), member-scoped.
  const [members, setMembers] = useState<{ id: string; name: string }[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!appSlug || !rt) return;
    let cancelled = false;
    portalApi
      .listMembers(appSlug)
      .then((ms) => {
        if (!cancelled) setMembers(ms.map((m) => ({ id: m.id, name: m.name })));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [appSlug, rt]);

  useEffect(() => {
    if (!appSlug || !tableId || !rowId) {
      setComments([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    portalApi
      .listActivity(appSlug, tableId, rowId)
      .then((rows) => {
        if (!cancelled) {
          setComments(rows);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setComments([]);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [appSlug, tableId, rowId]);

  function submit() {
    const text = draft.trim();
    if (!text || busy || !appSlug || !tableId || !rowId) return;
    // Derive mentions from the body: a member whose `@Name` still appears in
    // the text is mentioned (re-derived at submit so deletes/edits are honoured;
    // the server re-validates membership). The picker only ever offers members.
    const mentions = deriveMentions(text, members);
    setBusy(true);
    portalApi
      .addComment(appSlug, tableId, rowId, text, mentions)
      .then((c) => {
        setComments((cs) => [c, ...cs]);
        setDraft("");
        setMentionQuery(null);
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  }

  // Detect an active `@query` immediately before the caret to drive the picker.
  function onDraftChange(value: string, caret: number) {
    setDraft(value);
    setMentionQuery(activeMentionQuery(value.slice(0, caret)));
  }

  // Replace the active `@query` (just before the caret) with `@FullName `.
  function pickMention(member: { id: string; name: string }) {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : draft.length;
    setDraft(applyMention(draft.slice(0, caret), draft.slice(caret), member.name));
    setMentionQuery(null);
    requestAnimationFrame(() => ta?.focus());
  }

  const mentionMatches = matchMembers(mentionQuery, members);

  function remove(id: string) {
    if (!appSlug || !tableId) return;
    portalApi
      .deleteComment(appSlug, tableId, id)
      .then(() => setComments((cs) => cs.filter((c) => c.id !== id)))
      .catch(() => {});
  }

  // Builder preview / unbound → a placeholder (no record context at design time).
  if (!rt || !tableId || !rowId) {
    return (
      <div key={node.id} className="flex flex-col gap-1.5">
        {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
        <div className="rounded-[var(--portal-radius)] border border-dashed border-border px-3 py-5 text-center text-xs text-muted-foreground/70">
          Activity — the open record's comment timeline (bound at runtime)
        </div>
      </div>
    );
  }

  return (
    <div key={node.id} className="flex flex-col gap-3">
      {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
      <div className="relative flex flex-col gap-2">
        <textarea
          ref={taRef}
          className="w-full rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-2 text-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
          rows={2}
          placeholder="Aggiungi un commento… (usa @ per menzionare)"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value, e.target.selectionStart)}
        />
        {mentionMatches.length > 0 && (
          <div className="absolute left-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-[var(--portal-radius)] border border-border bg-surface shadow-lg">
            {mentionMatches.map((m) => (
              <button
                key={m.id}
                type="button"
                className="block w-full px-2.5 py-1.5 text-left text-sm hover:bg-muted"
                onMouseDown={(e) => {
                  e.preventDefault(); // keep focus in the textarea
                  pickMention(m);
                }}
              >
                {m.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            className="rounded-[var(--portal-radius)] bg-[var(--portal-accent,hsl(var(--accent)))] px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-50"
            disabled={!draft.trim() || busy}
            onClick={submit}
          >
            Comment
          </button>
        </div>
      </div>
      {loaded && comments.length === 0 ? (
        <div className="rounded-[var(--portal-radius)] border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          {str(node.props.empty_text, scope) || "No comments yet"}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {comments.map((c) => {
            const mine = myPrincipal != null && c.author === myPrincipal;
            // Prefer the server-resolved human name; fall back to the raw
            // principal (with the `app_user:` prefix stripped) only if missing.
            const who = mine ? "You" : c.author_label || c.author.replace(/^app_user:/, "");
            return (
              <li
                key={c.id}
                className="rounded-[var(--portal-radius)] border border-border bg-surface px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground">{who}</span>
                  <span className="flex items-center gap-2">
                    {c.at && (
                      <time className="text-[11px] text-muted-foreground">
                        {new Date(c.at).toLocaleString()}
                      </time>
                    )}
                    {mine && (
                      <button
                        type="button"
                        className="text-[11px] text-muted-foreground hover:text-danger"
                        onClick={() => remove(c.id)}
                      >
                        Delete
                      </button>
                    )}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{c.body}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RelatedList({
  node,
  scope,
  rt,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
}): ReactNode {
  const tableId = String(node.props.table_id ?? "");
  const relationField = String(node.props.relation_field ?? "");
  const rawParent = String(node.props.parent_id ?? "").trim() || "{{ $page.row }}";
  const parentId = str(rawParent, scope).trim();
  const columns = Array.isArray(node.props.columns) ? (node.props.columns as string[]) : [];
  const title = str(node.props.title, scope);
  const selectInto = String(node.props.select_into ?? "").trim();
  const actions = (node.props.row_actions as RowAction[] | undefined) ?? [];

  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loaded, setLoaded] = useState(false);
  const appSlug = rt?.appSlug ?? "";

  useEffect(() => {
    if (!appSlug || !tableId || !relationField || !parentId) {
      setRows([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    portalApi
      .queryRows(appSlug, tableId, {
        filters: [{ column: relationField, op: "eq", value: parentId }],
        page_size: 50,
      })
      .then((res) => {
        if (!cancelled) {
          setRows(res.rows as Record<string, unknown>[]);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [appSlug, tableId, relationField, parentId]);

  function runAction(a: RowAction, row: Record<string, unknown>) {
    if (!rt) return;
    const rowId = String(row.id ?? "");
    if (a.action === "select") {
      if (selectInto) rt.setVar(selectInto, row);
      return;
    }
    if (a.action === "delete") {
      if (!rowId) return;
      if (!window.confirm("Delete this record?")) return;
      portalApi
        .deleteRow(rt.appSlug, tableId, rowId)
        .then(() => setRows((rs) => rs.filter((r) => String(r.id ?? "") !== rowId)))
        .catch(() => {});
      return;
    }
    const target = a.target_page;
    if (!target) return;
    if ((a.open_mode ?? "drawer") === "drawer") rt.openDrawer(target, { row: rowId });
    else rt.navigate(portalRowPath(rt.appSlug, target, rowId));
  }

  // Unbound / builder preview → a placeholder (no parent context at design time).
  if (!rt || !tableId || !relationField || !parentId) {
    return (
      <div key={node.id} className="flex flex-col gap-1.5">
        {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
        <div className="rounded-[var(--portal-radius)] border border-dashed border-border px-3 py-5 text-center text-xs text-muted-foreground/70">
          Related list — bound at runtime (the record's child rows)
        </div>
      </div>
    );
  }

  const cols =
    columns.length > 0 ? columns : rows[0] ? Object.keys(rows[0]).filter((k) => k !== "__rel") : [];

  return (
    <div key={node.id} className="flex flex-col gap-2">
      {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
      {loaded && rows.length === 0 ? (
        <div className="rounded-[var(--portal-radius)] border border-dashed border-border px-3 py-5 text-center text-sm text-muted-foreground">
          {str(node.props.empty_text, scope) || "No related rows"}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--portal-radius)] border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {cols.map((c) => (
                  <th key={c} className="px-3 py-2 text-xs font-medium text-muted-foreground">
                    {humanizeColumn(c)}
                  </th>
                ))}
                {actions.length > 0 && <th className="px-3 py-1.5" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr
                  key={String(row.id ?? ri)}
                  onClick={selectInto ? () => rt.setVar(selectInto, row) : undefined}
                  className={`border-b border-border/60 last:border-0 ${selectInto ? "cursor-pointer hover:bg-muted/40" : ""}`}
                >
                  {cols.map((c) => (
                    <td key={c} className="px-3 py-1.5">
                      {renderCellValue(relValue(row, c))}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-1">
                        {actions.map((a, ai) => (
                          <button
                            key={ai}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              runAction(a, row);
                            }}
                            className="rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
                          >
                            {a.label || a.action || "Action"}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Month calendar — the bound query's rows placed on day cells by a date
 *  column. Month navigation is local view state (works in the builder too —
 *  it's not a data mutation). Clicking an event writes its row into
 *  `select_into` (master-detail). Weeks start on Monday. */
const CALENDAR_TONES: Record<string, string> = {
  accent: "bg-accent/15 text-accent",
  success: "bg-success/15 text-success",
  warning: "bg-warning/15 text-warning",
  danger: "bg-danger/15 text-danger",
};
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function Calendar({
  node,
  scope,
  rt,
}: {
  node: PageNode;
  scope: Scope;
  rt: PageRuntime | null;
}): ReactNode {
  const ref = effectiveQueryRef(node.props);
  const dateField = String(node.props.date_field ?? "");
  const titleField = String(node.props.title_field ?? "");
  const selectInto = String(node.props.select_into ?? "").trim();
  const toneCls = CALENDAR_TONES[String(node.props.tone ?? "accent")] ?? CALENDAR_TONES.accent;

  const q = (scope.$queries as Record<string, { rows: Record<string, unknown>[] }>)[ref];
  const rows = ref ? (q?.rows ?? []) : arbitraryDataRows(node, scope);

  // The displayed month — seeded from "today" once, then driven by prev/next.
  const [view, setView] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  if (!ref || !dateField) {
    return (
      <div
        key={node.id}
        className="flex h-48 w-full items-center justify-center rounded-[var(--portal-radius)] border border-dashed border-border text-xs text-muted-foreground/70"
      >
        Calendar — bind a query + date column
      </div>
    );
  }

  // Bucket events by their local YYYY-MM-DD; skip rows with no/invalid date.
  const byDay = new Map<string, Record<string, unknown>[]>();
  for (const row of rows) {
    const raw = row[dateField];
    if (raw == null || raw === "") continue;
    const d = new Date(String(raw));
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key)!.push(row);
  }

  const first = new Date(view.y, view.m, 1);
  const lead = (first.getDay() + 6) % 7; // Monday-first offset (getDay 0=Sun)
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
  const step = (delta: number) => {
    setView((v) => {
      const m = v.m + delta;
      return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
    });
  };

  return (
    <div key={node.id} className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-foreground">
          {MONTH_NAMES[view.m]} {view.y}
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => step(-1)}
            className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-surface"
            aria-label="Previous month"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setView(() => ({ y: today.getFullYear(), m: today.getMonth() }))}
            className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-surface"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-surface"
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 overflow-hidden rounded-[var(--portal-radius)] border border-border text-sm">
        {WEEKDAY_NAMES.map((w) => (
          <div
            key={w}
            className="border-b border-border bg-muted/30 px-2 py-1 text-[11px] font-medium text-muted-foreground"
          >
            {w}
          </div>
        ))}
        {cells.map((day, i) => {
          const key = day ? `${view.y}-${pad2(view.m + 1)}-${pad2(day)}` : "";
          const events = day ? (byDay.get(key) ?? []) : [];
          return (
            <div
              key={i}
              className={`min-h-[68px] border-b border-r border-border/60 p-1 ${day === null ? "bg-muted/10" : ""}`}
            >
              {day !== null && (
                <>
                  <div
                    className={`mb-0.5 text-[11px] ${key === todayKey ? "font-bold text-accent" : "text-muted-foreground"}`}
                  >
                    {day}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {events.slice(0, 3).map((row, ei) => (
                      <button
                        key={ei}
                        type="button"
                        onClick={() => {
                          if (rt && selectInto) rt.setVar(selectInto, row);
                        }}
                        title={String(relValue(row, titleField) ?? "")}
                        className={`truncate rounded px-1 py-0.5 text-left text-[11px] ${toneCls} ${selectInto && rt ? "cursor-pointer" : "cursor-default"}`}
                      >
                        {String(relValue(row, titleField) ?? "") || "—"}
                      </button>
                    ))}
                    {events.length > 3 && (
                      <span className="px-1 text-[10px] text-muted-foreground">
                        +{events.length - 3} more
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Resolve a `stat` delta's direction. `auto` reads the delta's leading sign
 *  (+ → up, - or the unicode minus → down, else neutral); an explicit
 *  up/down/none wins verbatim. Drives the delta colour + arrow. */
function statTrend(trend: string, delta: string): "up" | "down" | "none" {
  if (trend === "up" || trend === "down") return trend;
  if (trend === "none") return "none";
  const first = delta.trim()[0];
  if (first === "+") return "up";
  if (first === "-" || first === "−") return "down";
  return "none";
}

/** Does `node` or any of its descendants have this id? (Tabs uses it to reveal
 *  the tab a selected node lives in, in the builder.) */
function containsId(node: PageNode, id: string): boolean {
  if (!id) return false;
  if (node.id === id) return true;
  return (node.children ?? []).some((c) => containsId(c, id));
}

/** Tabbed container: a bar of the child `tab` nodes' labels + the active tab's
 *  panel. Active tab is local state at runtime; in the builder it follows the
 *  selection (so editing inside a tab reveals it) and a header click selects
 *  that tab. `rendered` is the already-wrapped children (1:1 with node.children). */
function Tabs({
  node,
  rendered,
  scope,
  editing,
}: {
  node: PageNode;
  rendered: ReactNode[];
  scope: Scope;
  editing?: EditCtx;
}): ReactNode {
  const [active, setActive] = useState(0);
  const n = node.children.length;
  if (n === 0) {
    // Builder: `rendered` is the empty drop zone; runtime: nothing to show.
    return (
      <div className="rounded-[var(--portal-radius)] border border-dashed border-border p-2">
        {rendered}
      </div>
    );
  }
  const selIdx = editing ? node.children.findIndex((c) => containsId(c, editing.selectedId)) : -1;
  const cur = selIdx >= 0 ? selIdx : Math.min(Math.max(0, active), n - 1);
  return (
    <div className="flex flex-col gap-3">
      <div role="tablist" className="flex flex-wrap items-center gap-1 border-b border-border">
        {node.children.map((c, i) => (
          // In the builder each header is a label-button + a delete "×"; nested
          // <button>s are invalid HTML, so the underline/colour ride on the
          // wrapper and the two controls sit side by side.
          <div
            key={c.id}
            role="tab"
            aria-selected={i === cur}
            className={`-mb-px flex items-center border-b-2 transition ${
              i === cur
                ? "border-accent text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <button
              type="button"
              onClick={(e) => {
                if (editing) {
                  e.stopPropagation();
                  editing.onSelect(c.id);
                }
                setActive(i);
              }}
              className={`py-1.5 pl-3 text-sm font-medium ${editing ? "pr-1" : "pr-3"}`}
            >
              {str(c.props.label, scope) || `Tab ${i + 1}`}
            </button>
            {editing && (
              <button
                type="button"
                title="Delete tab"
                onClick={(e) => {
                  e.stopPropagation();
                  editing.onDelete(c.id);
                }}
                className="px-1 text-base leading-none text-muted-foreground hover:text-danger"
              >
                ×
              </button>
            )}
          </div>
        ))}
        {editing && (
          // Add a tab without dragging the palette item: this is exactly a
          // palette drop of a fresh `tab` INTO this tabs node (placeDrop selects
          // the new tab, so the bar switches to it).
          <button
            type="button"
            title="Add tab"
            onClick={(e) => {
              e.stopPropagation();
              editing.onDrop("new:tab", node.id, "inside");
            }}
            className="-mb-px ml-1 border-b-2 border-transparent px-2 py-1.5 text-sm font-medium text-muted-foreground transition hover:text-foreground"
          >
            +
          </button>
        )}
      </div>
      <div>{rendered[cur]}</div>
    </div>
  );
}

/** Render a node, wrapping it in the edit overlay when in builder mode. The
 *  wrapper is a plain block element, so it stays a direct child of its parent
 *  flex/grid container and the layout is unchanged. */
function renderTree(
  node: PageNode,
  scope: Scope,
  rt: PageRuntime | null,
  editing?: EditCtx,
): ReactNode {
  // Data-driven conditional visibility: a `{{ }}` boolean expression hides the
  // node in the PORTAL when falsy (always shown in the builder so it stays
  // selectable/editable). Fail-open: an erroring expression keeps the node
  // visible rather than blanking the page on a typo.
  if (!editing && node.visible_when) {
    let visible = true;
    try {
      visible = Boolean(renderTemplate(node.visible_when, scope));
    } catch {
      visible = true;
    }
    if (!visible) return null;
  }
  const el = applyStyle(node, renderNode(node, scope, rt, editing), editing);
  // A `hidden` node is omitted entirely in the portal (applyStyle returns
  // null); in the builder it's kept (dimmed + selectable) so you can un-hide it.
  if (el === null) return null;
  if (!editing) return el;
  return (
    <EditNode key={node.id} node={node} editing={editing}>
      {el}
    </EditNode>
  );
}

/** Apply a node's shared style tokens (S15) by wrapping its rendered element
 *  in a styled box — but ONLY when the node actually carries style, so an
 *  un-styled node is byte-identical to before (zero extra DOM). `hidden`:
 *  portal → render nothing; builder → dim + keep selectable. Text-related
 *  tokens (align/size/weight/colour) cascade to the inner element. */
function applyStyle(node: PageNode, el: ReactNode, editing?: EditCtx): ReactNode {
  const style = node.style;
  if (!hasStyle(style)) return el;
  const hidden = isHidden(style);
  if (hidden && !editing) return null;
  const cls = styleToClasses(style);
  // Per-direction padding/margin ride as inline style (arbitrary numeric
  // values can't be Tailwind classes); universal tokens are classes.
  const inline = styleToInlineStyle(style);
  // The always-`hidden` token: portal omits the node (handled above); builder
  // dims it. Per-breakpoint responsive hiding is handled by `EditNode` (it
  // needs the selection state), so the preview can truly disappear the node
  // like the live portal would — see there.
  return (
    <div className={`${cls} ${hidden ? "opacity-40" : ""}`.trim()} style={inline}>
      {el}
    </div>
  );
}

function EditNode({
  node,
  editing,
  children,
}: {
  node: PageNode;
  editing: EditCtx;
  children: ReactNode;
}): ReactNode {
  const isRoot = node.id === "root";
  const selected = editing.selectedId === node.id;
  const isContainer = editing.allowsChildren(node.type);
  const [dropPos, setDropPos] = useState<DropPos | null>(null);
  const [hovered, setHovered] = useState(false);

  // Would the live portal hide this node at the current viewport-preview
  // breakpoint? Then mirror reality: actually drop it from the preview
  // (`display:none`) — UNLESS it's the selected node, which we keep visible
  // (dimmed) so it stays editable. Either way it's still reachable in the
  // Layers panel, so a hidden node is never lost.
  const hiddenAtBp = isHiddenAtBreakpoint(node.style, editing.previewBreakpoint);
  const dropFromPreview = hiddenAtBp && !selected;

  const horizontal = editing.horizontalAt(node.id);

  // Where would a drop land? Near an edge → sibling before/after; the middle of
  // a container → inside it. The root only accepts `inside`. Two extra cases:
  //  - inside a `columns` grid the axis is X (left/right reorder = before/after);
  //  - in a normal vertical stack, the far left/right 18% bands mean "split into
  //    columns" (`left`/`right`) — Elementor-style auto-wrap side-by-side.
  function computePos(e: DragEvent<HTMLDivElement>): DropPos {
    if (isRoot) return "inside";
    const rect = e.currentTarget.getBoundingClientRect();
    const rx = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
    if (horizontal) {
      if (isContainer) return rx < 0.25 ? "before" : rx > 0.75 ? "after" : "inside";
      return rx < 0.5 ? "before" : "after";
    }
    // Side bands → wrap into a columns grid (only meaningful for non-root nodes).
    if (rx < 0.18) return "left";
    if (rx > 0.82) return "right";
    const ry = rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0.5;
    if (isContainer) return ry < 0.25 ? "before" : ry > 0.75 ? "after" : "inside";
    return ry < 0.5 ? "before" : "after";
  }

  return (
    <div
      data-tray-node={node.id}
      // Drag to reorder: the root is a drop target (drop INTO the page) but is
      // never itself draggable.
      draggable={!isRoot}
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData("text/plain", node.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        const pos = computePos(e);
        if (pos !== dropPos) setDropPos(pos);
      }}
      onDragLeave={(e) => {
        e.stopPropagation();
        setDropPos(null);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const pos = dropPos ?? computePos(e);
        setDropPos(null);
        const payload = e.dataTransfer.getData("text/plain");
        if (payload) editing.onDrop(payload, node.id, pos);
      }}
      // Hover is tracked in JS (not CSS `:hover`) so ONLY the innermost node
      // under the cursor lights up — `stopPropagation` keeps the event from
      // bubbling to ancestor wrappers, which would otherwise all highlight.
      onMouseOver={(e) => {
        e.stopPropagation();
        if (!hovered) setHovered(true);
      }}
      onMouseOut={(e) => {
        e.stopPropagation();
        setHovered(false);
      }}
      onClick={(e) => {
        e.stopPropagation();
        editing.onSelect(node.id);
      }}
      className={`relative rounded-sm transition-shadow ${
        dropFromPreview ? "hidden " : ""
      }${hiddenAtBp && selected ? "opacity-50 " : ""}${
        dropPos === "inside"
          ? "bg-accent/5 ring-2 ring-inset ring-accent"
          : isRoot
            ? // The root page IS the surface — no card frame; a faint ring only
              // when selected so the inspector context is still discoverable.
              selected
              ? "ring-1 ring-inset ring-accent/20"
              : "ring-0"
            : selected
              ? "ring-2 ring-accent"
              : hovered
                ? "ring-1 ring-accent/40"
                : "ring-1 ring-transparent"
      } ${isRoot ? "" : "cursor-grab active:cursor-grabbing"}`}
    >
      {/* Insertion line — where a sibling drop will land. Vertical (left/right
          edge) inside a horizontal container, horizontal (top/bottom) else. */}
      {dropPos === "before" &&
        (horizontal ? (
          <div className="pointer-events-none absolute -left-px bottom-0 top-0 z-30 w-0.5 rounded bg-accent" />
        ) : (
          <div className="pointer-events-none absolute -top-px left-0 right-0 z-30 h-0.5 rounded bg-accent" />
        ))}
      {dropPos === "after" &&
        (horizontal ? (
          <div className="pointer-events-none absolute -right-px bottom-0 top-0 z-30 w-0.5 rounded bg-accent" />
        ) : (
          <div className="pointer-events-none absolute -bottom-px left-0 right-0 z-30 h-0.5 rounded bg-accent" />
        ))}
      {/* Side-band split → wrap into a columns grid: a full-height accent bar on
          the target edge + a faint accent wash to signal "side by side". */}
      {(dropPos === "left" || dropPos === "right") && (
        <>
          <div className="pointer-events-none absolute inset-0 z-20 rounded-sm bg-accent/5" />
          <div
            className={`pointer-events-none absolute bottom-0 top-0 z-30 w-1 rounded bg-accent ${
              dropPos === "left" ? "-left-px" : "-right-px"
            }`}
          />
        </>
      )}
      {/* Hover tag (not selected) — a small type-name chip top-left so you know
          what a click will select, without covering content. */}
      {!isRoot && hovered && !selected && (
        <span className="pointer-events-none absolute -top-[17px] left-0 z-30 rounded bg-accent/90 px-1.5 py-px text-[10px] font-medium leading-tight text-accent-foreground">
          {editing.labelFor(node.type)}
        </span>
      )}
      {/* Selected toolbar — one Elementor-style floating bar ABOVE the element:
          grip + type name, then move / duplicate / delete as real icons. Sits
          outside the box so it never overlaps the component's content. */}
      {!isRoot && selected && (
        <div className="absolute -top-[27px] left-0 z-40 flex items-center overflow-hidden rounded-md bg-accent text-accent-foreground shadow-md ring-1 ring-black/10">
          <span className="flex cursor-grab items-center gap-1 py-1 pl-1.5 pr-1 text-[11px] font-semibold leading-none active:cursor-grabbing">
            <GripVertical className="h-3 w-3 opacity-70" />
            {editing.labelFor(node.type)}
          </span>
          <span className="mr-0.5 h-4 w-px bg-accent-foreground/25" />
          <BarBtn title="Move up" onClick={() => editing.onMove(node.id, -1)}>
            <ArrowUp className="h-3.5 w-3.5" />
          </BarBtn>
          <BarBtn title="Move down" onClick={() => editing.onMove(node.id, 1)}>
            <ArrowDown className="h-3.5 w-3.5" />
          </BarBtn>
          <BarBtn title="Duplicate" onClick={() => editing.onDuplicate(node.id)}>
            <Copy className="h-3.5 w-3.5" />
          </BarBtn>
          <BarBtn title="Delete" danger onClick={() => editing.onDelete(node.id)}>
            <Trash2 className="h-3.5 w-3.5" />
          </BarBtn>
        </div>
      )}
      {children}
    </div>
  );
}

/** One icon button inside the selected node's floating accent toolbar. Sits on
 *  the accent bar, so hover/danger states use translucent overlays rather than
 *  theme text colours. */
function BarBtn({
  title,
  onClick,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      title={title}
      className={`flex h-6 w-6 items-center justify-center text-accent-foreground/80 transition-colors hover:text-accent-foreground ${
        danger ? "hover:bg-danger hover:text-white" : "hover:bg-white/20"
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

/** Builder-only placeholder shown inside an empty container — a big, obvious
 *  "drop here" target (events bubble to the container's EditNode, which lands
 *  the drop `inside`). */
function EmptyDropZone({ hint }: { hint?: string }): ReactNode {
  return (
    <div className="pointer-events-none rounded-md border border-dashed border-accent/40 bg-accent/5 px-4 py-6 text-center text-xs text-muted-foreground">
      {hint ?? "Drag a component here"}
    </div>
  );
}

function renderNode(
  node: PageNode,
  scope: Scope,
  rt: PageRuntime | null,
  editing?: EditCtx,
): ReactNode {
  let children = (node.children ?? []).map((c) => renderTree(c, scope, rt, editing));
  // Builder: an empty container shows a roomy drop placeholder so you can
  // clearly drop INTO it (a thin empty strip is impossible to target). The
  // repeater handles its own placeholder below.
  if (
    editing &&
    node.type !== "repeater" &&
    node.children.length === 0 &&
    editing.allowsChildren(node.type)
  ) {
    children = [<EmptyDropZone key="__empty" />];
  }
  // ADR-0044 U4 — resolve scalar `{{ }}` prop bindings ONCE so every
  // `node.props.X` read below sees the evaluated value (the runtime half of the
  // inspector's per-field JS toggle). Structural strings (`data`/`code`) and
  // non-bindings are untouched; see `resolveScalarBindings`.
  const resolved = resolveScalarBindings(node.props, scope);
  if (resolved !== node.props) node = { ...node, props: resolved };
  switch (node.type) {
    case "page": {
      // Page width resolution: an explicit `max_width` wins (a number → px,
      // "full"/"100%"/"none" → edge-to-edge); empty inherits the layout's
      // default via the `--portal-content-max` CSS var the shell sets (S8b).
      const raw = str(node.props.max_width, scope).trim();
      const maxWidth =
        raw === ""
          ? "var(--portal-content-max, 880px)"
          : /^(full|none|100%)$/i.test(raw)
            ? "none"
            : /^\d+$/.test(raw)
              ? `${raw}px`
              : raw;
      return (
        <div key={node.id} className="mx-auto w-full" style={{ maxWidth }}>
          <div className="flex flex-col gap-6">{children}</div>
        </div>
      );
    }
    case "modal":
    case "drawer": {
      // ADR-0045 — an overlay CONTAINER on the page (not a separate overlay
      // page). Open state: at runtime from `rt.isModalOpen(id)` (toggled by the
      // open_modal action, R2); in the BUILDER it shows whenever the node (or a
      // descendant) is selected, so its content is authorable in place.
      const isDrawer = node.type === "drawer";
      const title = str(node.props.title, scope).trim();
      const size = String(node.props.size ?? "md");
      const open = editing
        ? editing.selectedId === node.id || containsId(node, editing.selectedId)
        : rt
          ? rt.isModalOpen(node.id)
          : false;
      if (!open) return null; // hidden until opened (or selected in the builder)

      const MODAL_W: Record<string, string> = {
        sm: "max-w-md",
        md: "max-w-2xl",
        lg: "max-w-4xl",
        xl: "max-w-6xl",
      };
      const DRAWER_W: Record<string, string> = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-xl" };
      const widthCls = (isDrawer ? DRAWER_W[size] : MODAL_W[size]) ?? "max-w-md";
      const body = <div className="flex flex-col gap-3 overflow-y-auto p-4">{children}</div>;

      if (editing) {
        // Inline (in the page flow) so it's selectable + droppable; a chip marks
        // it as an overlay. R2 wires the open action; R3 makes the canvas run it.
        return (
          <div
            key={node.id}
            className="my-2 overflow-hidden rounded-[var(--portal-radius)] border border-accent/40 bg-background shadow-sm"
          >
            <div className="flex items-center gap-1.5 border-b border-border/60 bg-accent/5 px-3 py-1 text-[11px] font-medium text-accent">
              {isDrawer ? "Drawer" : "Modal"}
              {title ? ` · ${title}` : ""}
            </div>
            {body}
          </div>
        );
      }
      // Portal runtime: a real fixed overlay on a dim backdrop, closed by the X
      // or a backdrop click.
      const close = () => rt?.closeModal(node.id);
      return (
        <div
          key={node.id}
          className={`fixed inset-0 z-40 flex tray-light ${
            isDrawer ? "justify-end" : "items-center justify-center p-4"
          }`}
        >
          <div
            className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]"
            onClick={close}
            aria-hidden
          />
          <div
            role="dialog"
            aria-modal="true"
            className={`relative flex w-full flex-col overflow-hidden border border-border bg-background shadow-xl ${
              isDrawer ? `h-full ${widthCls}` : `max-h-[85vh] rounded-lg ${widthCls}`
            }`}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <span className="text-sm font-semibold">{title}</span>
              <button
                type="button"
                onClick={close}
                aria-label="Chiudi"
                className="rounded p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {body}
          </div>
        </div>
      );
    }
    case "container": {
      // The unified layout primitive (replaces section + columns): a flex/grid
      // box. `layout` picks the axis; gap / align / justify ride as style tokens
      // (componentStyleClasses("container", …)). Default md gap so an un-styled
      // container has breathing room like the old section/columns did.
      const layout = String(node.props.layout ?? "stack");
      const count = Math.max(1, Math.min(6, Number(node.props.columns ?? 2)));
      // Non-equal widths: an `fr` template (e.g. "2fr 1fr") is honoured ONLY when
      // safe (digits+fr) and its token count matches `columns`; else equal cols.
      const tpl = String(node.props.col_template ?? "").trim();
      const tplOk = /^\d+fr( \d+fr)*$/.test(tpl) && tpl.split(" ").length === count;
      const gridCols = tplOk ? tpl : `repeat(${count}, minmax(0, 1fr))`;
      const base = componentStyleClasses("container", node.style);
      const gapped = /\bgap-/.test(base) ? base : `gap-4 ${base}`.trim();
      const empty = node.children.length === 0;
      // Builder: an EMPTY container shows the layout structure so it never reads
      // as broken — a grid renders one ghost drop-cell PER column (so you see
      // "this is a 3-col grid"), stack/row a single roomy zone. The faint dashed
      // frame + min-height keep an otherwise invisible empty box discoverable.
      if (editing && empty) {
        const frame =
          "min-h-[72px] rounded-[var(--portal-radius)] p-2 outline-dashed outline-1 outline-border";
        if (layout === "grid") {
          return (
            <div
              key={node.id}
              className={`grid ${gapped} ${frame}`}
              style={{ gridTemplateColumns: gridCols }}
            >
              {Array.from({ length: count }).map((_, i) => (
                <EmptyDropZone key={i} hint="Drop here" />
              ))}
            </div>
          );
        }
        return (
          <div key={node.id} className={frame}>
            <EmptyDropZone hint={`Empty ${layout} — drag components here`} />
          </div>
        );
      }
      if (layout === "grid") {
        // Responsive by default: a multi-column grid STACKS to one column on a
        // narrow shell (mobile) and only applies its column template at/above
        // 640px of shell width. Without this a fixed `1fr 2fr` master-detail
        // overflowed off-screen on phones (unusable). Uses a container query on
        // the shell (`@container/shell`), NOT a viewport media query, so the
        // builder's device-preview honours it too. The template rides a CSS var
        // so an arbitrary `fr` template stays a JIT-safe literal class.
        return (
          <div
            key={node.id}
            // `.tray-grid-responsive` (globals.css) is a HAND-WRITTEN container
            // query on `@container/shell` — the Tailwind arbitrary-PROPERTY
            // container variant wasn't emitted, so multi-column containers were
            // stuck at one column on desktop. Mobile-first: 1 col, template ≥640px.
            className={`tray-grid-responsive ${gapped}`}
            style={{ ["--tray-grid-cols" as string]: gridCols } as CSSProperties}
          >
            {children}
          </div>
        );
      }
      if (layout === "row") {
        return (
          <div key={node.id} className={`flex flex-wrap ${gapped}`}>
            {children}
          </div>
        );
      }
      return (
        <div key={node.id} className={`flex flex-col ${gapped}`}>
          {children}
        </div>
      );
    }
    case "card": {
      const title = str(node.props.title, scope);
      const subtitle = str(node.props.subtitle, scope);
      return (
        <div
          key={node.id}
          className="overflow-hidden rounded-[var(--portal-radius)] border border-border bg-surface shadow-sm"
        >
          {(title || subtitle) && (
            <div className="border-b border-border px-5 py-3">
              {title && <h3 className="text-sm font-semibold tracking-tight">{title}</h3>}
              {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
            </div>
          )}
          <div className="flex flex-col gap-4 p-5">{children}</div>
        </div>
      );
    }
    case "tab": {
      // A tab's content panel. The parent `tabs` decides which one is visible;
      // standalone it just renders its stack.
      return (
        <div key={node.id} className="flex flex-col gap-4">
          {children}
        </div>
      );
    }
    case "tabs":
      return <Tabs key={node.id} node={node} rendered={children} scope={scope} editing={editing} />;
    case "page_ref":
      // ADR-0045 R4 — embed another page inline (portal: live + own runtime;
      // builder/preview: a placeholder). The page slug may be a `{{ }}` binding;
      // `params` is a {key: {{ }}} map evaluated against THIS page's scope and
      // seeded as the embedded page's read-only `$input` (e.g. a detail page
      // reads `{{ $input.id }}` from its host's selected row).
      return (
        <EmbeddedPage
          key={node.id}
          slug={str(node.props.page, scope)}
          params={renderValues(node.props.params, scope)}
          rt={rt}
          editing={editing}
        />
      );
    case "text": {
      const content = str(node.props.content, scope);
      if (!content && editing)
        return (
          <p
            key={node.id}
            className="rounded border border-dashed border-border px-2 py-1.5 text-xs italic text-muted-foreground/60"
          >
            Text — set its content
          </p>
        );
      return (
        <p key={node.id} className="text-sm leading-relaxed">
          {content}
        </p>
      );
    }
    case "heading": {
      const level = Math.max(1, Math.min(6, Number(node.props.level ?? 2)));
      const content = str(node.props.content, scope);
      if (!content && editing)
        return createElement(
          `h${level}`,
          {
            key: node.id,
            className:
              "rounded border border-dashed border-border px-2 py-1.5 text-sm font-semibold italic text-muted-foreground/60",
          },
          "Heading",
        );
      // Per-level type scale (Minimal/Linear): a real hierarchy instead of one
      // flat size for every heading.
      const HEADING_SCALE: Record<number, string> = {
        1: "text-2xl",
        2: "text-lg",
        3: "text-[0.8125rem] font-semibold uppercase tracking-wide text-muted-foreground",
        4: "text-sm",
        5: "text-sm",
        6: "text-xs",
      };
      const hClass =
        level === 3
          ? HEADING_SCALE[3]
          : `${HEADING_SCALE[level] ?? "text-base"} font-semibold tracking-tight text-foreground`;
      return createElement(`h${level}`, { key: node.id, className: hClass }, content);
    }
    case "image": {
      const src = str(node.props.src, scope);
      if (!src && editing)
        return (
          <div
            key={node.id}
            className="flex h-24 items-center justify-center rounded border border-dashed border-border text-xs text-muted-foreground/60"
          >
            Image — set its URL
          </div>
        );
      return (
        <img
          key={node.id}
          src={src}
          alt={str(node.props.alt, scope)}
          className={`max-w-full rounded ${componentStyleClasses("image", node.style)}`}
        />
      );
    }
    case "divider":
      return <hr key={node.id} className="border-border" />;
    case "stat": {
      const label = str(node.props.label, scope);
      const value = str(node.props.value, scope);
      const delta = str(node.props.delta, scope);
      const caption = str(node.props.caption, scope);
      const dir = statTrend(String(node.props.trend ?? "auto"), delta);
      const Icon = STAT_ICONS[String(node.props.icon ?? "none")];
      const deltaTone =
        dir === "up" ? "text-success" : dir === "down" ? "text-danger" : "text-muted-foreground";
      const Arrow = dir === "up" ? ArrowUp : dir === "down" ? ArrowDown : null;
      return (
        <div
          key={node.id}
          className="flex items-start justify-between gap-3 rounded-[var(--portal-radius)] border border-border bg-surface p-4 shadow-sm"
        >
          <div className="min-w-0">
            <div className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {label || (editing ? "Label" : "")}
            </div>
            <div className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
              {value || (editing ? "—" : "")}
            </div>
            {(delta || caption) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                {delta && (
                  <span className={`inline-flex items-center gap-0.5 font-medium ${deltaTone}`}>
                    {Arrow && <Arrow className="h-3 w-3" />}
                    {delta}
                  </span>
                )}
                {caption && <span className="text-muted-foreground">{caption}</span>}
              </div>
            )}
          </div>
          {Icon && (
            <span className="shrink-0 rounded-[var(--portal-radius)] bg-accent/10 p-2 text-accent">
              <Icon className="h-5 w-5" />
            </span>
          )}
        </div>
      );
    }
    case "callout": {
      const tone = CALLOUT_TONES[String(node.props.tone ?? "info")] ?? CALLOUT_TONES.info;
      const title = str(node.props.title, scope);
      const content = str(node.props.content, scope);
      const ToneIcon = tone.icon;
      return (
        <div
          key={node.id}
          className={`flex gap-3 rounded-[var(--portal-radius)] border px-4 py-3 ${tone.box}`}
        >
          <ToneIcon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.iconCls}`} />
          <div className="min-w-0 text-sm">
            {title && <div className="font-semibold">{title}</div>}
            <div className={`${title ? "mt-0.5 " : ""}leading-relaxed text-muted-foreground`}>
              {content || (editing && !title ? "Callout — set its content" : "")}
            </div>
          </div>
        </div>
      );
    }
    case "badge": {
      const text = str(node.props.text, scope);
      const cls = BADGE_TONES[String(node.props.tone ?? "neutral")] ?? BADGE_TONES.neutral;
      if (!text && editing)
        return (
          <span
            key={node.id}
            className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs italic text-muted-foreground/60"
          >
            Badge
          </span>
        );
      return (
        // `self-start` so a badge stays content-width inside a stack container
        // (a flex column stretches its children by default → a full-width pill).
        <span
          key={node.id}
          className={`inline-flex w-fit max-w-full items-center self-start rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}
        >
          {text}
        </span>
      );
    }
    case "progress": {
      const label = str(node.props.label, scope);
      const value = str(node.props.value, scope);
      const max = str(node.props.max, scope) || "100";
      const pct = progressPct(value, max);
      const fill = PROGRESS_TONES[String(node.props.tone ?? "accent")] ?? PROGRESS_TONES.accent;
      const showValue = node.props.show_value !== false;
      return (
        <div key={node.id} className="w-full">
          {(label || showValue) && (
            <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
              <span className="font-medium text-foreground">
                {label || (editing ? "Progress" : "")}
              </span>
              {showValue && (
                <span className="tabular-nums text-muted-foreground">{Math.round(pct)}%</span>
              )}
            </div>
          )}
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full transition-[width] ${fill}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      );
    }
    case "link": {
      const text = str(node.props.text, scope);
      const href = str(node.props.href, scope).trim();
      const target = String(node.props.target ?? "auto");
      // "external" = absolute URL or protocol-relative; everything else is an
      // in-portal path navigated via the SPA router (no full reload).
      const external = /^(https?:)?\/\//i.test(href) || href.startsWith("mailto:");
      const openBlank = target === "blank" || (target === "auto" && external);
      if (!text && editing)
        return (
          <span
            key={node.id}
            className="text-sm italic text-accent/60 underline underline-offset-2"
          >
            Link — set its text
          </span>
        );
      const cls =
        "inline-flex items-center gap-1 text-sm font-medium text-accent underline-offset-2 hover:underline";
      // Internal SPA navigation when a runtime is present and the link is local.
      if (rt && href && !external && !openBlank) {
        return (
          <a
            key={node.id}
            href={href}
            className={cls}
            onClick={(e) => {
              e.preventDefault();
              rt.navigate(href);
            }}
          >
            {text}
          </a>
        );
      }
      return (
        <a
          key={node.id}
          href={href || undefined}
          className={cls}
          target={openBlank ? "_blank" : undefined}
          rel={openBlank ? "noopener noreferrer" : undefined}
          // Inert in the builder preview (no navigation), still styled.
          onClick={editing ? (e) => e.preventDefault() : undefined}
        >
          {text}
          {openBlank && <ExternalLink className="h-3.5 w-3.5" />}
        </a>
      );
    }
    case "icon": {
      const Glyph = ICON_SET[String(node.props.name ?? "info")] ?? Info;
      const size = ICON_SIZES[String(node.props.size ?? "md")] ?? ICON_SIZES.md;
      const tone = ICON_TONES[String(node.props.tone ?? "default")] ?? "";
      return <Glyph key={node.id} className={`${size} ${tone}`.trim()} />;
    }
    case "chart":
      return <Chart key={node.id} node={node} scope={scope} rt={rt} editing={editing} />;
    case "custom_component":
      return (
        <CustomComponentFrame key={node.id} node={node} scope={scope} rt={rt} editing={editing} />
      );
    case "field": {
      const label = str(node.props.label, scope);
      const value = str(node.props.value, scope);
      if (!label && !value && editing)
        return (
          <div
            key={node.id}
            className="rounded border border-dashed border-border px-2 py-1.5 text-xs italic text-muted-foreground/60"
          >
            Field — set a label / value
          </div>
        );
      return (
        <div key={node.id} className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
          <span className="text-sm">{value}</span>
        </div>
      );
    }
    case "data_list":
      return <DataList key={node.id} node={node} scope={scope} rt={rt} />;
    case "kanban":
      return <Kanban key={node.id} node={node} scope={scope} rt={rt} />;
    case "related_list":
      return <RelatedList key={node.id} node={node} scope={scope} rt={rt} />;
    case "calendar":
      return <Calendar key={node.id} node={node} scope={scope} rt={rt} />;
    case "activity":
      return <ActivityPanel key={node.id} node={node} scope={scope} rt={rt} />;
    case "repeater": {
      // Renders its child template once per row of the bound query. Inside
      // the template, bindings use `$item` (the row) and `$index`.
      const ref = effectiveQueryRef(node.props);
      const q = (scope.$queries as Record<string, { rows: Record<string, unknown>[] }>)[ref];
      const rows = ref ? (q?.rows ?? []) : arbitraryDataRows(node, scope);
      const perRow = Math.max(1, Math.min(6, Number(node.props.per_row ?? 1)));
      const wrapClass = perRow > 1 ? "grid gap-4" : "flex flex-col gap-4";
      const wrapStyle =
        perRow > 1 ? { gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` } : undefined;

      // Builder: render the template ONCE (sample = first row) so it stays a
      // single editable card; a dashed frame signals that it repeats.
      if (editing) {
        const sample = rows[0] ?? {};
        const childScope: Scope = { ...scope, $item: sample, $index: 0 };
        return (
          <div key={node.id} className="rounded-md border border-dashed border-border/70 p-2">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Repeats per row · template
            </div>
            <div className="flex flex-col gap-4">
              {node.children.length > 0 ? (
                node.children.map((c) => renderTree(c, childScope, rt, editing))
              ) : (
                <EmptyDropZone hint="Drag a component here, then bind it with {{ $item.column }}" />
              )}
            </div>
          </div>
        );
      }
      if (rows.length === 0) {
        return (
          <p key={node.id} className="text-sm italic text-muted-foreground">
            {str(node.props.empty_text, scope) || "No data."}
          </p>
        );
      }
      return (
        <div key={node.id} className={wrapClass} style={wrapStyle}>
          {rows.map((row, i) => (
            <div key={String(row.id ?? i)}>
              {node.children.map((c) =>
                renderTree(c, { ...scope, $item: row, $index: i }, rt, editing),
              )}
            </div>
          ))}
        </div>
      );
    }
    case "button":
      return (
        <button
          key={node.id}
          type="button"
          // Builder: not disabled (so it isn't greyed) but pointer-events-none,
          // so a click selects the node instead of firing the (inert) action.
          // Size + width come from the component-specific style tokens (S15);
          // their defaults reproduce the original `w-fit px-3 py-1.5 text-sm`.
          className={`inline-flex items-center justify-center gap-1.5 rounded-[var(--portal-radius)] font-medium shadow-sm outline-none transition active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-accent/30 disabled:opacity-50 ${BUTTON_VARIANTS[String(node.props.variant ?? "primary")] ?? BUTTON_VARIANTS.primary} ${componentStyleClasses(
            "button",
            node.style,
          )} ${editing ? "pointer-events-none" : ""}`}
          disabled={!rt && !editing}
          onClick={
            rt
              ? () => void runActions(node.props.on_click, buildActionContext(rt, scope))
              : undefined
          }
        >
          {str(node.props.label, scope) || "Button"}
        </button>
      );
    case "form":
      // Interactive only inside the portal runtime; the builder preview
      // shows a placeholder (no runtime → no app slug to submit to).
      // In a drawer (create flow): Save → close the drawer, which refreshes the
      // underlying list. On a normal page: just refresh in place.
      return rt ? (
        <PortalFormBlock
          key={node.id}
          appSlug={rt.appSlug}
          formRef={String(node.props.form_id ?? "")}
          // `prefill` (a {col: "{{ }}"} map) injects + HIDES a field — a form
          // opened in a drawer for a known parent (e.g. the current model) needn't
          // re-ask it. Evaluated against the drawer's `$page` (seeded by open_drawer vars).
          prefill={renderValues(node.props.prefill, scope)}
          onSubmitted={() => (rt.closeDrawer ? rt.closeDrawer() : rt.refresh())}
        />
      ) : (
        <div
          key={node.id}
          className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground"
        >
          Form (interactive in the portal)
        </div>
      );
    case "file_viewer": {
      // Inline preview of an app `file`-column value (PDF/image), + a download.
      // The `file` prop is a raw {{ }} binding to the descriptor OBJECT — read
      // it via `tmpl` (type-preserving) so it isn't stringified.
      const fileVal = tmpl(node.props.file, scope);
      const vh = Number(str(node.props.height, scope)) || undefined;
      const vEmpty = str(node.props.empty_text, scope) || undefined;
      // `show_download` (default true) — off = a bare thumbnail (no toolbar),
      // for a gallery of reference photos where a per-card download is noise.
      const vShowDownload = node.props.show_download !== false;
      if (!rt) {
        return (
          <div
            key={node.id}
            className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground"
          >
            File viewer (anteprima nel portale)
          </div>
        );
      }
      return (
        <PortalFileViewer
          key={node.id}
          appSlug={rt.appSlug}
          file={fileVal}
          height={vh}
          emptyText={vEmpty}
          showDownload={vShowDownload}
        />
      );
    }
    case "record":
      // Edit one row, bound by id (usually a {{ $page.row }} binding).
      return rt ? (
        <PortalRecordBlock
          key={node.id}
          appSlug={rt.appSlug}
          tableId={String(node.props.table_id ?? "")}
          columns={Array.isArray(node.props.columns) ? (node.props.columns as string[]) : []}
          // Default to the page's row var (how drawers/detail pages are opened)
          // — a template-generated record without an explicit binding must not
          // render "No record selected." (mirrors ActivityPanel's default).
          rowId={str(String(node.props.row_id ?? "").trim() || "{{ $page.row }}", scope)}
          allowDelete={node.props.allow_delete === true}
          layoutColumns={node.props.layout_columns === 2 ? 2 : 1}
          readonly={node.props.readonly === true}
          onChanged={rt.refresh}
        />
      ) : (
        <div
          key={node.id}
          className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground"
        >
          Record editor (interactive in the portal)
        </div>
      );
    case "text_input":
    case "number_input":
    case "checkbox_input":
    case "select_input":
    case "textarea_input":
      // Inputs (S13): bound to page state; interactive in the portal,
      // pointer-events-none in the builder (so a click selects the node).
      return <PortalInput key={node.id} node={node} scope={scope} rt={rt} builder={!!editing} />;
    case "page_filter":
      // Dashboard-wide filter control (3b): sets the page GLOBAL filter so
      // every block re-queries scoped to it. Runtime-only.
      return <PortalPageFilter key={node.id} node={node} rt={rt} builder={!!editing} />;
    default:
      return (
        <div key={node.id} className="text-xs text-danger">
          Unknown component: {node.type}
        </div>
      );
  }
}

export function PageRenderer({
  result,
  editing,
  rt: rtOverride,
}: {
  result: PortalPageResult;
  /** Builder-only: wraps each node in a selectable overlay. Omit in the
   *  portal — the runtime render is then byte-identical. */
  editing?: EditCtx;
  /** Builder running-canvas (ADR-0045 R3): an explicit sandbox runtime that
   *  overrides the context — lets the builder render an INTERACTIVE preview
   *  (buttons run their actions, modals open) without a portal session. The
   *  portal omits it and reads the runtime from context. */
  rt?: PageRuntime;
}): ReactNode {
  const ctxRt = useOptionalRuntime();
  const rt = rtOverride ?? ctxRt;
  // ADR-0046 — load the app's external JS libraries (runtime-CDN) once; `undefined`
  // until ready (bindings/JS-Objects that use them resolve empty, then light up on
  // the re-render the hook triggers when loaded).
  const lib = useAppLibraries(result.jsLibraries);
  const scope = buildScope(result, rt, lib);
  // ADR-0046 — expose the app's library descriptors to any `custom_component`
  // frame in the tree (BOTH planes): the sandboxed frame loads them itself from
  // their CDN url. One provider here covers portal + builder.
  return (
    <CustomComponentLibrariesContext.Provider value={result.jsLibraries ?? []}>
      {renderTree(result.page.ast.root, scope, rt, editing)}
    </CustomComponentLibrariesContext.Provider>
  );
}
